// ============================================
// FILE: LearningSupportsQuestionnaire.jsx
// React Component for Learning Supports Questionnaire
// ============================================

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import "./homestyle.css";
import { LearningSupports, mapDesignationToCategoryLabel } from "../../utils/QuestionsList";
import { useChatContext } from "../../context/ChatContext";
import { processLearningSupportsDocuments, generateStrengthsNeedsDesignationAligned,
  generateMeasurableGoals, generateSupportsServices,
  generateCoreCompetencyGoals,
  generateCurricularCompetencyGoals,
  formatLearningSupportsAiError } from "../../utils/LearningSupportsApi";
import { validateLearningSupportDocuments } from "../../utils/documentUploadValidation";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TextareaAutosize from "react-textarea-autosize";

const formatAnswersAsPlainText = (questionsList, answers) => {
  return questionsList
    .filter((q) => q.type !== "info" && (answers[q.question] || "").trim() !== "")
    .map((q) => {
      const ans = answers[q.question].trim();
      return `Q: ${q.question}\nA: ${ans}\n`;
    })
    .join("\n");
};

// Helper function to mask student name: first letter of each word + X's, with space between words (e.g. "Raj Sharma" -> "RXX SXXXX")
// Preserves spaces while typing so user can see "RXX " when they type "Raj "
const maskStudentName = (name) => {
  if (!name || typeof name !== 'string') return '';
  // Split but keep spaces (e.g. "Raj " -> ["Raj", " "], "Raj  Sharma" -> ["Raj", "  ", "Sharma"])
  const parts = name.split(/(\s+)/);
  return parts
    .map(part => {
      if (/^\s+$/.test(part)) return part; // keep spaces as-is
      const lettersOnly = part.replace(/[^a-zA-Z]/g, '');
      if (!lettersOnly.length) return part;
      if (lettersOnly.length === 1) return lettersOnly.charAt(0).toUpperCase();
      return lettersOnly.charAt(0).toUpperCase() + 'X'.repeat(lettersOnly.length - 1);
    })
    .join('');
};

const QUESTIONNAIRE_HELPER_TEXT_STYLE = {
  fontSize: "13px",
  color: "#555",
  marginBottom: "10px",
  marginTop: 0,
  padding: "10px 12px",
  backgroundColor: "#f0f7ff",
  borderRadius: "8px",
  borderLeft: "3px solid #2c68a1",
  whiteSpace: "pre-line",
  lineHeight: 1.5,
};

const AI_GENERATE_QUESTION_KEYS = new Set([
  "student_profile_core_competency_goals",
  "student_profile_curricular_competency_goals",
  "strengths_needs_designation_aligned",
  "measurable_strength_based_goals",
  "supports_accommodations_services",
]);

/** Resolve unmasked student name from Vision extraction (before Student Details masking). */
const resolveExtractedStudentName = (extractedAnswers) => {
  if (!extractedAnswers || typeof extractedAnswers !== "object") return "";
  let name = (
    extractedAnswers["student_profile_first_last_name"] ??
    extractedAnswers["Student First Name/Last Name"] ??
    ""
  )
    .toString()
    .replace(/[^a-zA-Z\s]/g, "")
    .trim();
  if (!name) {
    const first = (
      extractedAnswers["Student First Name"] ??
      extractedAnswers["student_first_name"] ??
      ""
    )
      .toString()
      .replace(/[^a-zA-Z\s]/g, "")
      .trim();
    const last = (
      extractedAnswers["Student Last Name"] ??
      extractedAnswers["student_last_name"] ??
      ""
    )
      .toString()
      .replace(/[^a-zA-Z\s]/g, "")
      .trim();
    name = [first, last].filter(Boolean).join(" ");
  }
  return name;
};

/** True if token looks like already-masked name piece (JXX, GXXXXXX). */
const isMaskedNameToken = (token) => {
  const t = String(token || "").replace(/[^a-zA-Z]/g, "");
  return t.length >= 2 && /^[A-Za-z]X+$/i.test(t);
};

const NAME_INFER_STOPWORDS = new Set([
  "the", "and", "for", "with", "from", "this", "that", "student", "grade", "needs", "strengths",
  "domain", "communication", "academic", "physical", "social", "emotional", "behaviour", "behavior",
  "functional", "mild", "moderate", "intense", "learning", "disorder", "reading", "writing", "math",
  "projects", "activities", "basketball", "swimming", "crossfit", "others", "company", "topics",
  "interest", "choices", "instruction", "tourettes", "adhd", "sample", "ispp", "iep", "pdf", "docx",
  "doc", "form", "survey", "support", "plan", "school", "teacher", "parent", "guardian", "september",
  "january", "february", "march", "april", "june", "july", "august", "october", "november", "december"
]);

/** Infer student first name from repeated Capitalized tokens in narrative text (e.g. Joe appears many times). */
const inferStudentNameFromTexts = (texts) => {
  const counts = Object.create(null);
  (texts || []).forEach((text) => {
    if (!text || typeof text !== "string") return;
    const matches = text.match(/\b[A-Z][a-z]{2,}\b/g) || [];
    matches.forEach((w) => {
      if (NAME_INFER_STOPWORDS.has(w.toLowerCase())) return;
      if (isMaskedNameToken(w)) return;
      counts[w] = (counts[w] || 0) + 1;
    });
  });
  let best = "";
  let bestCount = 0;
  Object.entries(counts).forEach(([w, c]) => {
    if (c > bestCount) {
      best = w;
      bestCount = c;
    }
  });
  return bestCount >= 2 ? best : "";
};

/** Infer a likely first name from uploaded file names (e.g. "ISPP sample Joe 3 1.pdf"). */
const inferStudentNameFromFileNames = (fileNames) => {
  const counts = Object.create(null);
  (fileNames || []).forEach((name) => {
    if (!name || typeof name !== "string") return;
    const base = name.replace(/\.[^.]+$/, "");
    const matches = base.match(/\b[A-Z][a-z]{2,}\b/g) || [];
    matches.forEach((w) => {
      if (NAME_INFER_STOPWORDS.has(w.toLowerCase())) return;
      counts[w] = (counts[w] || 0) + 1;
    });
  });
  let best = "";
  let bestCount = 0;
  Object.entries(counts).forEach(([w, c]) => {
    if (c > bestCount) {
      best = w;
      bestCount = c;
    }
  });
  return best || "";
};

/** Collect narrative strings from extraction payload (strengths/needs etc.) for name inference. */
const collectNarrativeTextsFromExtraction = (extractedAnswers) => {
  const texts = [];
  const visit = (val) => {
    if (val == null) return;
    if (typeof val === "string") {
      if (val.trim().length > 8) texts.push(val);
      return;
    }
    if (Array.isArray(val)) {
      val.forEach(visit);
      return;
    }
    if (typeof val === "object") {
      Object.entries(val).forEach(([k, v]) => {
        const key = String(k).toLowerCase();
        if (
          key.includes("strength") ||
          key.includes("need") ||
          key.includes("summary") ||
          key.includes("finding") ||
          key.includes("goal") ||
          key.includes("comment") ||
          key.includes("note")
        ) {
          visit(v);
        } else if (typeof v === "object") {
          visit(v);
        } else if (typeof v === "string" && v.trim().length > 20) {
          texts.push(v);
        }
      });
    }
  };
  visit(extractedAnswers);
  return texts;
};

/**
 * Resolve best unmasked name for redaction: real extracted name, else inferred from text/filenames.
 * Ignores already-masked values like JXX GXXXXXX so scrub can still find "Joe".
 */
const resolveStudentNameForRedaction = (extractedAnswers, fileNames = []) => {
  let name = resolveExtractedStudentName(extractedAnswers);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length && parts.every((p) => isMaskedNameToken(p))) {
    name = "";
  }
  if (!name) {
    name = inferStudentNameFromTexts(collectNarrativeTextsFromExtraction(extractedAnswers));
  }
  if (!name) {
    name = inferStudentNameFromFileNames(fileNames);
  }
  return name;
};

/**
 * Replace student proper name(s) in free text with the same masked form as Student Details
 * (e.g. "Joe enjoys..." → "JXX enjoys..." when real name is "Joe" / "Joe Gomez").
 */
const redactStudentNamesInText = (text, realName) => {
  if (!text || typeof text !== "string" || !realName) return text;
  const cleaned = realName.replace(/[^a-zA-Z\s]/g, "").trim();
  if (!cleaned) return text;
  const words = cleaned.split(/\s+/).filter((w) => w.length >= 2);
  const tokens = [...new Set([cleaned, ...words])].sort((a, b) => b.length - a.length);
  let result = text;
  for (const token of tokens) {
    if (isMaskedNameToken(token)) continue;
    const masked = maskStudentName(token);
    if (!masked || masked.toLowerCase() === token.toLowerCase()) continue;
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(`\\b${escaped}\\b`, "gi"), masked);
  }
  return result;
};

/** Normalize Vision API payload whether answers are flat or nested under extracted_answers. */
const normalizeExtractedAnswersPayload = (response) => {
  const root = response?.extracted_answers;
  if (!root || typeof root !== "object") return {};
  const nested = root.extracted_answers;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...root, ...nested };
  }
  return root;
};

const LearningSupportsQuestionnaire = () => {
  const {
    setTitleId,
    setInitialStart,
    setHighLightBtn
  } = useChatContext();

  const questionsList = [...LearningSupports];
  const FinalButton = "Learning Supports";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [multiSelectValues, setMultiSelectValues] = useState({}); // For multi_select and checkbox types
  const [domainRatings, setDomainRatings] = useState({}); // For domain_ratings type
  const [strengthsNeedsTable, setStrengthsNeedsTable] = useState({}); // For strengths_needs_table: key -> { domainKey: { strength, need, priority } }
  const [otherTextValues, setOtherTextValues] = useState({}); // For "other" text inputs
  const [supportEstimatorValues, setSupportEstimatorValues] = useState({}); // For support_estimator type
  const [isppFindings, setIsppFindings] = useState({}); // For ispp_findings type: { key: { primaryDomains: [], impairmentRating: '', keyStrengths: '', primaryNeeds: '', recommendedStrategies: '' } }
  const [categoryFundingValues, setCategoryFundingValues] = useState({}); // For category_funding: { key: { category: '', categoryOther: '' } }
  const [addClass, setAddClass] = useState(false);
  // Q.4 ISPP key redacted details: 'static' = no PDF data / manual flow, 'dynamic' = table filled from PDF extraction
  const [isppDomainSummaryTableType, setIsppDomainSummaryTableType] = useState("static");
  // Dynamic domain rows from API (preserves original domain names e.g. "Self Determination / Independence")
  const [isppDomainSummaryDynamicRows, setIsppDomainSummaryDynamicRows] = useState([]);

  // PDF upload states (multiple documents, each < 20 MB)
  const [isUploadingPDF, setIsUploadingPDF] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]); // Files attached via Attachment button; processing runs immediately
  const [showUploadStep, setShowUploadStep] = useState(true); // One-time optional upload before questions

  useLayoutEffect(() => {
    const sidebar = document.getElementById("sidebar");
    const content = document.getElementById("content");
    const header = document.getElementById("header");
    if (sidebar?.classList.contains("open")) {
      content?.classList.add("shrink");
      header?.classList.add("shrink");
    }
  }, []);

  const [, setIsGeneratingAlignment] = useState(false);
  const [generatingAIForKeys, setGeneratingAIForKeys] = useState([]); // array of question keys currently generating — each button disabled only when its key is in the list
  const attachmentInputRef = useRef(null);
  const processingAbortRef = useRef(null); // AbortController for current processing; new upload cancels previous
  const MAX_FILE_SIZE_MB = 20;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const navigate = useNavigate();

  // Helper function to check if we should treat has_designation as "no"
  // This handles the case where user selects "unsure" then "continue without designation"
  const shouldTreatDesignationAsNo = () => {
    const designationQuestion = questionsList.find(dq => dq.key === "has_designation");
    if (!designationQuestion) return false;
    
    const designationAnswer = answers[designationQuestion.question];
    if (!designationAnswer) return false;
    
    const answerLower = designationAnswer.toLowerCase().trim();
    
    // Direct "no" answer
    if (answerLower === "no") {
      return true;
    }
    
    // "unsure" + clarification to continue without designation
    if (answerLower === "not sure" || answerLower === "unsure") {
      const clarificationQuestion = questionsList.find(dq => dq.key === "clarification_choice");
      if (clarificationQuestion) {
        const clarificationAnswer = answers[clarificationQuestion.question];
        if (clarificationAnswer) {
          const clarificationLower = clarificationAnswer.toLowerCase();
          if (clarificationLower.includes("continue without designation") || 
              clarificationLower.includes("support-based path") ||
              clarificationLower.includes("continue_no")) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  // Helper: check if answer matches required value (for a single dependsOn key)
  const answerMatchesValue = (answer, valueLower) => {
    if (!answer || answer.trim() === "") return false;
    const answerLower = answer.toLowerCase().trim();
    if (valueLower === "yes") {
      return answerLower === "yes" || answerLower.includes("yes");
    }
    return answerLower === valueLower || answerLower.includes(valueLower);
  };

  // Filter questions based on conditional logic (supports dependsOn + dependsOnValue, and dependsOnOr)
  const getFilteredQuestionsList = () => {
    const filtered = [];
    
    for (let i = 0; i < questionsList.length; i++) {
      const q = questionsList[i];
      
      // dependsOnOr: show question if ANY of the conditions match
      if (q.dependsOnOr && Array.isArray(q.dependsOnOr)) {
        let anyMatch = false;
        for (const cond of q.dependsOnOr) {
          const refQ = questionsList.find(dq => dq.key === cond.key || dq.question === cond.key);
          if (!refQ) continue;
          const refAnswer = answers[refQ.question];
          if (answerMatchesValue(refAnswer, (cond.value || "").toLowerCase().trim())) {
            anyMatch = true;
            break;
          }
        }
        if (anyMatch) {
          filtered.push(q);
        }
        continue;
      }
      
      if (!q.dependsOn) {
        filtered.push(q);
        continue;
      }
      
      // Find the question this depends on (check both key and question text)
      const dependsOnQuestion = filtered.find(dq => 
        dq.key === q.dependsOn || dq.question === q.dependsOn
      ) || questionsList.find(dq => 
        dq.key === q.dependsOn || dq.question === q.dependsOn
      );
      
      if (!dependsOnQuestion) {
        filtered.push(q);
        continue;
      }
      
      const dependsOnAnswer = answers[dependsOnQuestion.question];
      if (!dependsOnAnswer || dependsOnAnswer.trim() === "") {
        continue; // Skip this question
      }
      
      // Check if answer matches the required value
      if (q.dependsOnValue) {
        const valueLower = q.dependsOnValue.toLowerCase().trim();
        // Special handling: if dependsOn is "has_designation" and value is "no",
        // check both direct "no" answer and "unsure" + "continue_no" path
        if (q.dependsOn === "has_designation" && valueLower === "no") {
          if (shouldTreatDesignationAsNo()) {
            filtered.push(q);
          }
        } else if (answerMatchesValue(dependsOnAnswer, valueLower)) {
          filtered.push(q);
        }
      } else {
        // If no specific value required, just check if answer exists
        filtered.push(q);
      }
    }
    
    return filtered;
  };

  const filteredQuestionsList = getFilteredQuestionsList();
  const currentQuestion = filteredQuestionsList[currentIndex];
  const currentAnswer = answers[currentQuestion?.question] || "";

  // Section 4 — show all Student Profile questions in one screen (one below the other)
  const section4StartIndex = filteredQuestionsList.findIndex((q) => q.key === "student_profile_first_last_name");
  const section5StartIndex = filteredQuestionsList.findIndex((q) => q.key === "strengths_needs_designation_aligned");
  const section4EndIndex = section5StartIndex >= 0 ? section5StartIndex : filteredQuestionsList.length;
  const section4Questions = useMemo(
    () => (section4StartIndex >= 0 ? filteredQuestionsList.slice(section4StartIndex, section4EndIndex) : []),
    [section4StartIndex, section4EndIndex, filteredQuestionsList]
  );
  // Use section4Questions as-is so Core/Curricular Competency goals stay in place (mandatory but position unchanged)
  const section4QuestionsSorted = section4Questions;
  const isShowingSection4Block = section4Questions.length > 0 && currentIndex === section4StartIndex;

  const isQuestionRequired = (question) => {
    if (!question) return false;
    if (question.key === "student_profile_parent_consulted_date") {
      const consultationQuestion = section4Questions.find((q) => q.key === "student_profile_parent_consultation");
      const consultationAnswer = (consultationQuestion && answers[consultationQuestion.question]) || "";
      return consultationAnswer.toLowerCase().trim() === "yes";
    }
    return question.important === "true";
  };

  // IEP Date * always defaults to current date when empty; only manual change overrides (autofill does not set IEP Date)
  const getTodayMMDDYYYY = () => {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
  };
  useEffect(() => {
    if (!isShowingSection4Block || !section4Questions.length) return;
    const iepQuestion = section4Questions.find((q) => q.key === "student_profile_iep_date" && q.defaultValue === "today");
    if (!iepQuestion) return;
    const currentIepAnswer = (answers[iepQuestion.question] || "").trim();
    if (!currentIepAnswer) {
      setAnswers((prev) => ({ ...prev, [iepQuestion.question]: getTodayMMDDYYYY() }));
    }
  }, [isShowingSection4Block, section4Questions, answers]);

  // After Section 4 block (Q.5), next question shows as Q.6, not Q.12
  const displayQuestionNumber = (section4StartIndex >= 0 && currentIndex >= section4EndIndex)
    ? section4StartIndex + 2 + (currentIndex - section4EndIndex)
    : currentIndex + 1;
  const isAnswerFilled = currentQuestion?.type === "info" ||
    currentAnswer.trim() !== "" ||
    (currentQuestion?.type === "multi_select" && multiSelectValues[currentQuestion?.key]?.length > 0) ||
    (currentQuestion?.type === "checkbox" && multiSelectValues[currentQuestion?.key]?.length > 0) ||
    (currentQuestion?.type === "domain_ratings" && Object.keys(domainRatings[currentQuestion?.key] || {}).length > 0) ||
    (currentQuestion?.type === "strengths_needs_table" && (
      currentQuestion?.key === "ispp_domain_summary" && isppDomainSummaryTableType === "dynamic" && isppDomainSummaryDynamicRows.length > 0
        ? isppDomainSummaryDynamicRows.some((row) => (row?.strength || row?.need)?.trim())
        : Object.values(strengthsNeedsTable[currentQuestion?.key] || {}).some((row) => (row?.strength || row?.need)?.trim())
    )) ||
    (currentQuestion?.type === "support_estimator" && currentAnswer.trim() !== "") ||
    (currentQuestion?.type === "ispp_findings" && (() => {
      const data = isppFindings[currentQuestion?.key] || {};
      return (data.primaryDomains?.length > 0 && data.impairmentRating?.trim() !== "");
    })()) ||
    (currentQuestion?.type === "category_funding" && (() => {
      const data = categoryFundingValues[currentQuestion?.key] || {};
      const cat = (data.category || "").trim();
      const catOther = (data.categoryOther || "").trim();
      return (cat !== "" && (cat !== "Other" || catOther !== ""));
    })());

  // Restore multi-select state when navigating (use " | " delimiter so labels containing commas are preserved)
  const CHECKBOX_ANSWER_DELIM = " | ";
  useEffect(() => {
    if (currentQuestion?.type === "multi_select" || currentQuestion?.type === "checkbox") {
      const questionKey = currentQuestion?.key;
      if (questionKey) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.trim()) {
          const labels = existingAnswer.split(CHECKBOX_ANSWER_DELIM).map(l => l.trim()).filter(Boolean);
          const restored = currentQuestion.options
            .filter(opt => {
              return labels.some(label =>
                label === opt.label ||
                (opt.value === "other" && label.startsWith("Other:"))
              );
            })
            .map(opt => ({ value: opt.value, label: opt.label }));
          setMultiSelectValues(prev => ({
            ...prev,
            [questionKey]: restored
          }));
          const otherEntry = labels.find(l => l.startsWith("Other:"));
          if (otherEntry) {
            const otherText = otherEntry.replace("Other:", "").trim();
            setOtherTextValues(prev => ({ ...prev, [questionKey]: otherText }));
          }
        }
      }
    } else if (currentQuestion?.type === "strengths_needs_table") {
      const questionKey = currentQuestion?.key;
      const hasExistingTableData = questionKey && Object.keys(strengthsNeedsTable[questionKey] || {}).length > 0;
      if (questionKey && currentQuestion?.domains && !hasExistingTableData) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.trim()) {
          const table = {};
          const parts = existingAnswer.split("; ");
          parts.forEach((part) => {
            const match = part.match(/^(.+?): Strength: (.+?), Need: (.+?), Priority: (.+)$/);
            if (match) {
              const [, domainName, strength, need, priority] = match;
              const domain = currentQuestion.domains.find((d) => d.name === domainName.trim());
              if (domain) {
                table[domain.key] = {
                  strength: strength?.trim() || "",
                  need: need?.trim() || "",
                  priority: priority?.trim() || ""
                };
              }
            }
          });
          if (Object.keys(table).length > 0) {
            setStrengthsNeedsTable((prev) => ({ ...prev, [questionKey]: table }));
          }
        }
      }
    }
     
    else if (currentQuestion?.type === "domain_ratings") {
      const questionKey = currentQuestion?.key;
      if (questionKey) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.trim()) {
          const ratings = {};
          const parts = existingAnswer.split("; ");
          parts.forEach(part => {
            const [domainPart, ratingPart] = part.split(": ");
            if (domainPart && ratingPart) {
              const domain = currentQuestion.domains.find(d => d.name === domainPart.trim());
              if (domain) {
                const rating = currentQuestion.ratingOptions.find(r => r.label === ratingPart.trim());
                if (rating) {
                  ratings[domain.key] = { value: rating.value, label: rating.label };
                }
              }
            }
          });
          setDomainRatings(prev => ({
            ...prev,
            [questionKey]: ratings
          }));
        }
      }
    } else if (currentQuestion?.type === "select" && currentQuestion?.allowOther) {
      const questionKey = currentQuestion?.key;
      if (questionKey) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.startsWith("Other:")) {
          const otherText = existingAnswer.replace(/^Other:\s*/, "");
          setOtherTextValues(prev => ({ ...prev, [questionKey]: otherText }));
        }
      }
    } else if (currentQuestion?.type === "category_funding") {
      const questionKey = currentQuestion?.key;
      if (questionKey) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.trim()) {
          // Handle both old format "Category: X, Funding Level: Y" and new format "X" or "Other: X"
          let catPart = existingAnswer.trim();
          // Remove "Category:" prefix if present
          catPart = catPart.replace(/^Category:\s*/i, "");
          // Remove funding level suffix if present (for backward compatibility)
          catPart = catPart.replace(/,?\s*Funding Level:\s*.+$/i, "").trim();
          
          // Try to map to a standard category option
          const categoryLabel = mapDesignationToCategoryLabel(catPart, currentQuestion?.categoryOptions);
          const finalCategory = categoryLabel || catPart;
          const finalIsOther = finalCategory.startsWith("Other");
          
          setCategoryFundingValues(prev => ({
            ...prev,
            [questionKey]: {
              category: finalIsOther ? "Other" : finalCategory,
              categoryOther: finalIsOther ? finalCategory.replace(/^Other:?\s*/, "") : ""
            }
          }));
        }
      }
    }
  }, [currentIndex, currentQuestion, answers, strengthsNeedsTable]);

  const handleInputChange = (e) => {
    setAnswers({
      ...answers,
      [currentQuestion?.question]: e.target.value,
    });
  };

  const generateAIApiMap = {
    strengths_needs_designation_aligned: generateStrengthsNeedsDesignationAligned,
    measurable_strength_based_goals: generateMeasurableGoals,
    supports_accommodations_services: generateSupportsServices,
    student_profile_core_competency_goals: generateCoreCompetencyGoals,
    student_profile_curricular_competency_goals: generateCurricularCompetencyGoals
  };

  // Table passed to API: include dynamic ISPP rows (by domain name) when applicable
  const effectiveStrengthsNeedsTable = useMemo(() => {
    if (isppDomainSummaryTableType === "dynamic" && isppDomainSummaryDynamicRows.length > 0) {
      return {
        ...strengthsNeedsTable,
        ispp_domain_summary: Object.fromEntries(
          isppDomainSummaryDynamicRows.map((r, i) => [
            (r.domain || `domain_${i}`).trim(),
            { strength: r.strength || "", need: r.need || "", priority: r.priority || "" }
          ])
        )
      };
    }
    return strengthsNeedsTable;
  }, [strengthsNeedsTable, isppDomainSummaryTableType, isppDomainSummaryDynamicRows]);

  const handleGenerateAI = async () => {
    const key = currentQuestion?.key;
    if (!generateAIApiMap[key]) return;
    const hasProfileData = Object.values(answers || {}).some((v) => String(v || "").trim() !== "");
    if (!hasProfileData) {
      toast.error("Please complete earlier questionnaire sections before using Generate with AI.");
      return;
    }
    setIsGeneratingAlignment(true);
    setGeneratingAIForKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    try {
      const apiCall = generateAIApiMap[key];
      const generated = await apiCall({
        answers,
        strengthsNeedsTable: effectiveStrengthsNeedsTable,
        questionsList
      });
      if (generated) {
        if (/^not sufficient data\.?$/i.test(generated.trim())) {
          toast.warning("Not enough student information yet. Please complete more questionnaire fields, then try again.");
        } else {
          setAnswers((prev) => ({ ...prev, [currentQuestion.question]: generated }));
        }
      } else {
        toast.warning("No content was generated. Please try again.");
      }
    } catch (err) {
      console.error("Generate AI error:", err);
      toast.error(formatLearningSupportsAiError(err));
    } finally {
      setIsGeneratingAlignment(false);
      setGeneratingAIForKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  // Section 4: Generate AI for a specific question (e.g. Core/Curricular Competency goals)
  const handleGenerateAIForSection4 = async (q) => {
    if (!generateAIApiMap[q.key]) return;
    const hasProfileData = Object.values(answers || {}).some((v) => String(v || "").trim() !== "");
    if (!hasProfileData) {
      toast.error("Please complete earlier questionnaire sections before using Generate with AI.");
      return;
    }
    setIsGeneratingAlignment(true);
    setGeneratingAIForKeys((prev) => (prev.includes(q.key) ? prev : [...prev, q.key]));
    try {
      const apiCall = generateAIApiMap[q.key];
      const generated = await apiCall({ answers, strengthsNeedsTable: effectiveStrengthsNeedsTable, questionsList });
      if (generated) {
        if (/^not sufficient data\.?$/i.test(generated.trim())) {
          toast.warning("Not enough student information yet. Please complete more questionnaire fields, then try again.");
        } else {
          setAnswers((prev) => ({ ...prev, [q.question]: generated }));
        }
      } else {
        toast.warning("No content was generated. Please try again.");
      }
    } catch (err) {
      console.error("Generate AI error:", err);
      toast.error(formatLearningSupportsAiError(err));
    } finally {
      setIsGeneratingAlignment(false);
      setGeneratingAIForKeys((prev) => prev.filter((k) => k !== q.key));
    }
  };

  // Handle text input with validation (e.g. student name / initials: only alphabets and spaces)
  const handleTextInputChange = (e) => {
    const value = e.target.value;
    // Student First Name/Last Name: only letters and spaces (no numbers), mask display (e.g. "RXX SXXXX")
    if (currentQuestion?.key === "student_profile_first_last_name") {
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      setAnswers({
        ...answers,
        [currentQuestion?.question]: maskStudentName(filteredValue), // Store and display masked only
      });
      return;
    }
    // For student initials, only allow alphabets and spaces
    if (currentQuestion?.key === "student_initials") {
      // Allow only letters and spaces, convert to uppercase
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
      setAnswers({
        ...answers,
        [currentQuestion?.question]: filteredValue,
      });
    } else {
      // For other text inputs, allow normal input
      setAnswers({
        ...answers,
        [currentQuestion?.question]: value,
      });
    }
  };

  // Handle number input with validation for student age (no negative, no 'e')
  const handleNumberInputChange = (e) => {
    const value = e.target.value;
    // For student age, prevent negative numbers and 'e' character
    if (currentQuestion?.key === "student_age") {
      // Remove 'e', 'E', '+', '-' and any non-numeric characters except decimal point
      let filteredValue = value.replace(/[eE+-]/g, '');
      // Only allow positive integers (no decimals for age)
      filteredValue = filteredValue.replace(/[^0-9]/g, '');
      // Prevent negative by ensuring it's always positive
      const numValue = parseInt(filteredValue) || '';
      setAnswers({
        ...answers,
        [currentQuestion?.question]: numValue.toString(),
      });
    } else {
      // For other number inputs, allow normal input
      setAnswers({
        ...answers,
        [currentQuestion?.question]: value,
      });
    }
  };

  // Handle checkbox/multi-select toggle
  const handleCheckboxToggle = (optionValue, optionLabel) => {
    const questionKey = currentQuestion?.key;
    const questionText = currentQuestion?.question;
    if (!questionKey || !questionText) return;

    setMultiSelectValues((prev) => {
      const currentSelections = prev[questionKey] || [];
      const isSelected = currentSelections.some((item) => item.value === optionValue);

      let updatedSelections;

      // Make "none" mutually exclusive with other options
      if (optionValue === "none") {
        // If "none" is being selected, clear all others and keep only "none"
        updatedSelections = isSelected
          ? []
          : [{ value: optionValue, label: optionLabel }];
      } else {
        // If a non-"none" option is being toggled, remove "none" if it was selected
        const withoutNone = currentSelections.filter((item) => item.value !== "none");
        updatedSelections = isSelected
          ? withoutNone.filter((item) => item.value !== optionValue)
          : [...withoutNone, { value: optionValue, label: optionLabel }];
      }

      if (optionValue === "other" && isSelected) {
        setOtherTextValues((o) => ({ ...o, [questionKey]: "" }));
      }

      const otherText = otherTextValues[questionKey];
      const CHECKBOX_DELIM = " | ";
      const answerText = updatedSelections
        .map((item) => {
          if (item.value === "other" && otherText) return `Other: ${otherText}`;
          return item.label;
        })
        .join(CHECKBOX_DELIM);
      setAnswers((a) => ({ ...a, [questionText]: answerText }));

      return { ...prev, [questionKey]: updatedSelections };
    });
  };
  const handleCheckboxRowClick = (optionValue, optionLabel) => (e) => {
    if (e.target.type === "checkbox" || e.target.tagName === "INPUT") return;
    e.preventDefault();
    e.stopPropagation();
    handleCheckboxToggle(optionValue, optionLabel);
  };

  // Handle radio button selection
  const handleRadioChange = (optionValue, optionLabel) => {
    setAnswers({
      ...answers,
      [currentQuestion?.question]: optionLabel
    });
  };

  // Handle ISPP domain summary dynamic table (by row index) – preserves API domain names
  const handleIsppDomainSummaryDynamicChange = (index, field, value) => {
    const question = questionsList.find((q) => q.key === "ispp_domain_summary");
    if (!question?.question) return;
    setIsppDomainSummaryDynamicRows((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      const answerStr = next
        .map((r) => {
          const s = (r.strength || "").trim();
          const n = (r.need || "").trim();
          if (!s && !n) return null;
          return `${r.domain}: Strength: ${s || ""}, Need: ${n || ""}, Priority: ${(r.priority || "").trim() || "—"}`;
        })
        .filter(Boolean)
        .join("; ");
      setAnswers((a) => ({ ...a, [question.question]: answerStr }));
      return next;
    });
  };

  // Handle strengths_needs_table cell change
  const handleStrengthsNeedsTableChange = (questionKey, domainKey, field, value) => {
    const question = questionsList.find((q) => q.key === questionKey);
    if (!question?.question || !question.domains) return;
    setStrengthsNeedsTable((prev) => {
      const table = prev[questionKey] || {};
      const row = table[domainKey] || {};
      const nextTable = { ...table, [domainKey]: { ...row, [field]: value } };
      const answerStr = question.domains
        .map((d) => {
          const r = nextTable[d.key] || {};
          const s = (r.strength || "").trim();
          const n = (r.need || "").trim();
          const p = (r.priority || "").trim();
          if (!s && !n) return null;
          return `${d.name}: Strength: ${s || ""}, Need: ${n || ""}, Priority: ${p || "—"}`;
        })
        .filter(Boolean)
        .join("; ");
      setAnswers((a) => ({ ...a, [question.question]: answerStr }));
      return { ...prev, [questionKey]: nextTable };
    });
  };

  // Handle domain rating change
  const handleDomainRatingChange = (domainKey, ratingValue, ratingLabel) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;

    const currentRatings = domainRatings[questionKey] || {};
    currentRatings[domainKey] = { value: ratingValue, label: ratingLabel };

    setDomainRatings({
      ...domainRatings,
      [questionKey]: currentRatings
    });

    // Format answer text
    const answerText = Object.entries(currentRatings)
      .map(([domain, rating]) => {
        const domainName = currentQuestion.domains.find(d => d.key === domain)?.name || domain;
        return `${domainName}: ${rating.label}`;
      })
      .join("; ");

    setAnswers({
      ...answers,
      [currentQuestion?.question]: answerText
    });
  };

  // Handle "other" text input
  const handleOtherTextChange = (questionKey, value) => {
    setOtherTextValues({
      ...otherTextValues,
      [questionKey]: value
    });

    // Update answer if "other" is selected
    const currentSelections = multiSelectValues[questionKey] || [];
    const otherSelected = currentSelections.some(item => item.value === "other");
    if (otherSelected) {
      const otherText = value ? `Other: ${value}` : "Other";
      const answerText = currentSelections
        .filter(item => item.value !== "other")
        .map(item => item.label)
        .concat(value ? [otherText] : ["Other"])
        .join(", ");
      setAnswers({
        ...answers,
        [currentQuestion?.question]: answerText
      });
    }
  };

  // Handle support estimator input
  const handleSupportEstimatorChange = (fieldKey, value) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;

    const currentValues = supportEstimatorValues[questionKey] || {};
    const updatedValues = {
      ...currentValues,
      [fieldKey]: value ? parseInt(value) || 0 : 0
    };

    setSupportEstimatorValues({
      ...supportEstimatorValues,
      [questionKey]: updatedValues
    });

    // Calculate total hours per year
    const totalMinutesPerWeek = Object.values(updatedValues).reduce((sum, val) => sum + (val || 0), 0);
    const totalHoursPerYear = (totalMinutesPerWeek * 40) / 60; // Assuming 40 weeks per school year

    // Format answer
    const breakdown = currentQuestion.fields
      .map(field => `${field.label}: ${updatedValues[field.key] || 0} ${field.unit}`)
      .join("; ");
    
    const answerText = `${breakdown} | Total: ${totalHoursPerYear.toFixed(1)} hours/year`;
    
    setAnswers({
      ...answers,
      [currentQuestion?.question]: answerText
    });
  };

  // Check if option is selected (for checkbox/multi_select)
  const isOptionSelected = (optionValue) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return false;
    const selections = multiSelectValues[questionKey] || [];
    return selections.some(item => item.value === optionValue);
  };

  // Get domain rating for a specific domain
  const getDomainRating = (domainKey) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return null;
    return domainRatings[questionKey]?.[domainKey]?.value || null;
  };

  // ISPP findings: format structured data to string and sync to answers
  const syncIsppFindingsToAnswer = (questionKey, questionText, data) => {
    const parts = [];
    if (data.primaryDomains?.length) parts.push("Primary Domains Assessed: " + data.primaryDomains.join(", "));
    if (data.impairmentRating) parts.push("Impairment Rating: " + data.impairmentRating);
    if (data.keyStrengths?.trim()) parts.push("Key Strengths: " + data.keyStrengths.trim());
    if (data.primaryNeeds?.trim()) parts.push("Primary Needs: " + data.primaryNeeds.trim());
    if (data.recommendedStrategies?.trim()) parts.push("Recommended Strategies/Supports: " + data.recommendedStrategies.trim());
    const str = parts.join(". ");
    setAnswers((prev) => ({ ...prev, [questionText]: str }));
  };

  const handleIsppPrimaryDomainToggle = (label) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;
    const data = isppFindings[questionKey] || {};
    const list = data.primaryDomains || [];
    const next = list.includes(label) ? list.filter((l) => l !== label) : [...list, label];
    const nextData = { ...data, primaryDomains: next };
    setIsppFindings((prev) => ({ ...prev, [questionKey]: nextData }));
    syncIsppFindingsToAnswer(questionKey, currentQuestion?.question, nextData);
  };

  const handleIsppImpairmentRating = (label) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;
    const data = isppFindings[questionKey] || {};
    const nextData = { ...data, impairmentRating: label };
    setIsppFindings((prev) => ({ ...prev, [questionKey]: nextData }));
    syncIsppFindingsToAnswer(questionKey, currentQuestion?.question, nextData);
  };

  const handleIsppTextChange = (field, value) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;
    const data = isppFindings[questionKey] || {};
    const nextData = { ...data, [field]: value };
    setIsppFindings((prev) => ({ ...prev, [questionKey]: nextData }));
    syncIsppFindingsToAnswer(questionKey, currentQuestion?.question, nextData);
  };

  const handleNext = (important) => {
    setAddClass(true);

    // When on Section 4 block: validate all required Section 4 questions, then jump to next section
    if (isShowingSection4Block) {
      const missingRequired = section4Questions.filter(
        (q) => isQuestionRequired(q) && !(answers[q.question] || "").trim()
      );
      if (missingRequired.length > 0) {
        toast.error("Please fill all required fields (marked with *) in Student Profile before proceeding.");
        return;
      }
      if (section4EndIndex >= filteredQuestionsList.length) {
        const finalAnswers = { ...answers };
        const formatedata = formatAnswersAsPlainText(filteredQuestionsList, finalAnswers);
        navigate("/afterchatbot", { state: { answers: finalAnswers, formatedata, fromLearningSupports: true } });
        return;
      }
      setCurrentIndex(section4EndIndex);
      return;
    }

    // Validation for required questions
    if (!isAnswerFilled && important === "true") {
      const isChoiceQuestion = ["radio", "select", "category_funding", "checkbox", "multi_select"].includes(currentQuestion?.type);
      toast.error(
        isChoiceQuestion
          ? "Please select an answer before proceeding."
          : "Please enter an answer before proceeding."
      );
      return;
    }

    // ISPP is optional – user may proceed (Next/Continue) even if ISPP not yet completed.

    // If Instructional Support Check = No → end flow; student does not require IEP
    if (currentQuestion?.key === "instructional_support_check" && currentAnswer === "No") {
      toast.info("This student does not currently require an IEP. Use the Home button to exit.");
      return;
    }

    if (currentIndex < filteredQuestionsList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      const finalAnswers = { ...answers };
      const formatedata = formatAnswersAsPlainText(filteredQuestionsList, finalAnswers);
      console.log("answers", finalAnswers);
      console.log("formatedata", formatedata);
      navigate("/afterchatbot", { state: { answers: finalAnswers, formatedata, fromLearningSupports: true } });
    }
  };

  // Run processing with given file list. Cancels any previous request. Caller must pass current list (add or remove updates list then calls this).
  const runProcessWithFiles = async (filesToProcess) => {
    if (!filesToProcess || filesToProcess.length === 0) {
      setUploadedFileNames([]);
      setIsUploadingPDF(false);
      return;
    }
    if (processingAbortRef.current) {
      processingAbortRef.current.abort();
      processingAbortRef.current = null;
    }
    const controller = new AbortController();
    processingAbortRef.current = controller;
    setIsUploadingPDF(true);
    try {
      const response = await processLearningSupportsDocuments(filesToProcess, questionsList, controller.signal);
      if (controller.signal.aborted) return;
      const extractedAnswers = normalizeExtractedAnswersPayload(response);
      const fileNameList = filesToProcess.map((f) => f.name);
      const studentRealName = resolveStudentNameForRedaction(extractedAnswers, fileNameList);
      const allNames = fileNameList.map(
        (name) => redactStudentNamesInText(name, studentRealName) || name
      );
      setUploadedFileNames(allNames);
      if (Object.keys(extractedAnswers).length > 0) {
        autoFillForm(extractedAnswers, true, fileNameList);
        toast.success(allNames.length > 1
          ? `${allNames.length} documents processed. Form has been filled.`
          : "Document processed. Form has been filled.");
      } else {
        toast.warning("Document(s) processed but no answers were extracted. Please fill manually.");
      }
    } catch (error) {
      if (controller.signal.aborted || error.name === "CanceledError" || error.name === "AbortError" || error.code === "ERR_CANCELED") return;
      console.error("Error processing document(s):", error);
      toast.error(
        formatLearningSupportsAiError(
          error,
          "We couldn't read this document right now. Please try again."
        )
      );
    } finally {
      if (processingAbortRef.current === controller) {
        setIsUploadingPDF(false);
        processingAbortRef.current = null;
      }
    }
  };

  // Attachment: add files → update list → cancel previous API → process all current documents immediately
  const handleAttachmentAdd = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    const validation = validateLearningSupportDocuments(fileList);
    if (validation.rejected) {
      toast.error(
        `"${validation.fileName}" was rejected. Only upload documents about individual student learning support (e.g. ISPP forms, assessments, parent surveys, teacher observation notes). Do not upload sports, newsletters, receipts, or team photos.`
      );
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      return;
    }
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const allowedExtensions = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
    for (const file of fileList) {
      const hasValidType = allowedTypes.includes(file.type) ||
        allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
      if (!hasValidType) {
        toast.error(`"${file.name}" is not allowed. Use PDF, Word, JPG, JPEG, or PNG only.`);
        if (attachmentInputRef.current) attachmentInputRef.current.value = "";
        return;
      }
    }
    const currentTotal = attachedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const newTotal = fileList.reduce((sum, f) => sum + (f.size || 0), 0);
    if (currentTotal + newTotal > MAX_FILE_SIZE_BYTES) {
      const totalMB = ((currentTotal + newTotal) / (1024 * 1024)).toFixed(1);
      toast.error(`Total size of all documents is ${totalMB} MB. Keep total under ${MAX_FILE_SIZE_MB} MB.`);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      return;
    }
    const newList = [...attachedFiles, ...fileList];
    setAttachedFiles(newList);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    runProcessWithFiles(newList);
  };

  // Attachment: remove one file → update list → cancel previous → process remaining (if any)
  const removeAttachment = (index) => {
    const newList = attachedFiles.filter((_, i) => i !== index);
    setAttachedFiles(newList);
    runProcessWithFiles(newList);
  };

  // Attachment: remove all → cancel and clear
  const clearAllAttachments = () => {
    if (processingAbortRef.current) {
      processingAbortRef.current.abort();
      processingAbortRef.current = null;
    }
    setAttachedFiles([]);
    setUploadedFileNames([]);
    setIsUploadingPDF(false);
    toast.info("All attachments removed.");
  };

  // Skip only obvious placeholder/fake values (n/a, unknown, etc.) – allow short/handwritten real answers
  const isPlaceholderOnly = (value) => {
    if (value == null) return true;
    const s = String(value).trim().toLowerCase();
    if (!s) return true;
    const placeholders = ["n/a", "na", "none", "not found", "unknown", "not applicable", "null", "n.a."];
    return placeholders.some((x) => s === x || s === x + ".");
  };

  // Format any date string to MM/DD/YYYY (mmddyyyy) for all date fields in the form/results
  const formatDateToMMDDYYYY = (str) => {
    if (str == null || (typeof str !== "string" && typeof str !== "number")) return "";
    const s = String(str).trim();
    if (!s) return "";
    // Already MM/DD/YYYY or MMDDYYYY (8 digits)
    const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, mm, dd, yyyy] = slashMatch;
      return `${mm.padStart(2, "0")}/${dd.padStart(2, "0")}/${yyyy}`;
    }
    if (/^\d{8}$/.test(s)) return `${s.slice(0, 2)}/${s.slice(2, 4)}/${s.slice(4, 8)}`;
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [, y, m, d] = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return `${m}/${d}/${y}`;
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    return s;
  };

  // Convert stored date (MM/DD/YYYY or any format) to YYYY-MM-DD for type="date" input so the calendar works; we still store MM/DD/YYYY.
  const toDateInputValue = (str) => {
    if (str == null || (typeof str !== "string" && typeof str !== "number")) return "";
    const s = String(str).trim();
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, mm, dd, yyyy] = slashMatch;
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }
    if (/^\d{8}$/.test(s)) return `${s.slice(4, 8)}-${s.slice(0, 2)}-${s.slice(2, 4)}`;
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split("T")[0];
    return "";
  };

  // Auto-fill form based on extracted answers. When clearFirst is true, all form data is cleared first then only extracted data is filled.
  const autoFillForm = (extractedAnswers, clearFirst = false, fileNames = []) => {
    const newAnswers = clearFirst ? {} : { ...answers };
    const newMultiSelectValues = clearFirst ? {} : { ...multiSelectValues };
    const newOtherTextValues = clearFirst ? {} : { ...otherTextValues };
    const newDomainRatings = clearFirst ? {} : { ...domainRatings };
    const newStrengthsNeedsTable = clearFirst ? {} : { ...strengthsNeedsTable };
    const newSupportEstimatorValues = clearFirst ? {} : { ...supportEstimatorValues };
    const newCategoryFundingValues = clearFirst ? {} : { ...categoryFundingValues };
    let isppTableFilledFromPdf = false;
    let newIsppDomainSummaryDynamicRows;
    // Same privacy rule as Student Details: scrub proper names from all autofilled free text
    const studentRealName = resolveStudentNameForRedaction(extractedAnswers, fileNames);
    const scrub = (value) => redactStudentNamesInText(value, studentRealName);

    questionsList.forEach((question) => {
      // IEP Date: always use current date unless user manually changes; do not set from autofill
      if (question.key === "student_profile_iep_date") return;

      // API may return strengths_needs_by_domain for the ISPP table (key ispp_domain_summary)
      let extractedValue =
        extractedAnswers[question.key] ||
        extractedAnswers[question.question] ||
        (question.key === "ispp_domain_summary" ? extractedAnswers["strengths_needs_by_domain"] : undefined);
      if (question.key === "student_profile_dob" && extractedValue == null) {
        extractedValue = extractedAnswers["dob"] ?? extractedAnswers["date_of_birth"] ?? extractedAnswers["Date of Birth"];
      }

      if (!extractedValue) return;
      if (isPlaceholderOnly(extractedValue)) return;

      // Handle different question types
      if (question.type === "radio" || question.type === "select") {
        // Find matching option
        const matchingOption = question.options?.find(opt => {
          const optLabel = opt.label?.toLowerCase().trim();
          const extracted = String(extractedValue).toLowerCase().trim();
          return optLabel === extracted || optLabel.includes(extracted) || extracted.includes(optLabel);
        });
        
        if (matchingOption) {
          newAnswers[question.question] = matchingOption.label;
        } else if (question.allowOther) {
          newAnswers[question.question] = "Other";
          newOtherTextValues[question.key] = scrub(String(extractedValue));
        }
      } 
      else if (question.type === "checkbox" || question.type === "multi_select") {
        // Handle multiple selections – drop only obvious placeholders from list
        const extractedArray = Array.isArray(extractedValue) 
          ? extractedValue 
          : String(extractedValue).split(",").map(s => s.trim());
        const validItems = extractedArray.filter((s) => s && !isPlaceholderOnly(s));
        
        const selectedOptions = [];
        validItems.forEach(extracted => {
          const matchingOption = question.options?.find(opt => {
            const optLabel = opt.label?.toLowerCase().trim();
            const extractedLower = String(extracted).toLowerCase().trim();
            return optLabel === extractedLower || optLabel.includes(extractedLower) || extractedLower.includes(optLabel);
          });
          
          if (matchingOption && matchingOption.value !== "other") {
            selectedOptions.push({ value: matchingOption.value, label: matchingOption.label });
          } else if (question.allowOther) {
            selectedOptions.push({ value: "other", label: "Other" });
            newOtherTextValues[question.key] = scrub(String(extracted));
          }
        });
        
        if (selectedOptions.length > 0) {
          newMultiSelectValues[question.key] = selectedOptions;
          const answerText = selectedOptions
            .map(item => {
              if (item.value === "other" && newOtherTextValues[question.key]) {
                return `Other: ${newOtherTextValues[question.key]}`;
              }
              return item.label;
            })
            .join(", ");
          newAnswers[question.question] = answerText;
        }
      }
      else if (question.type === "domain_ratings") {
        // Handle domain ratings – ignore placeholder-only values
        if (typeof extractedValue === "object" && question.domains) {
          const ratings = {};
          question.domains.forEach(domain => {
            const domainValue = extractedValue[domain.key] || extractedValue[domain.name];
            if (domainValue && !isPlaceholderOnly(domainValue)) {
              const matchingRating = question.ratingOptions?.find(rating => {
                const ratingLabel = rating.label?.toLowerCase().trim();
                const extracted = String(domainValue).toLowerCase().trim();
                return ratingLabel === extracted || ratingLabel.includes(extracted);
              });
              
              if (matchingRating) {
                ratings[domain.key] = { value: matchingRating.value, label: matchingRating.label };
              }
            }
          });
          
          if (Object.keys(ratings).length > 0) {
            newDomainRatings[question.key] = ratings;
            const answerText = Object.entries(ratings)
              .map(([domainKey, rating]) => {
                const domainName = question.domains.find(d => d.key === domainKey)?.name || domainKey;
                return `${domainName}: ${rating.label}`;
              })
              .join("; ");
            newAnswers[question.question] = answerText;
          }
        }
      }
      else if (question.type === "support_estimator") {
        // Handle support estimator
        if (typeof extractedValue === "object" && question.fields) {
          const values = {};
          question.fields.forEach(field => {
            const fieldValue = extractedValue[field.key] || extractedValue[field.label];
            if (fieldValue) {
              values[field.key] = parseInt(fieldValue) || 0;
            }
          });
          
          if (Object.keys(values).length > 0) {
            newSupportEstimatorValues[question.key] = values;
            const totalMinutesPerWeek = Object.values(values).reduce((sum, val) => sum + (val || 0), 0);
            const totalHoursPerYear = (totalMinutesPerWeek * 40) / 60;
            const breakdown = question.fields
              .map(field => `${field.label}: ${values[field.key] || 0} ${field.unit}`)
              .join("; ");
            newAnswers[question.question] = `${breakdown} | Total: ${totalHoursPerYear.toFixed(1)} hours/year`;
          }
        }
      }
      else if (question.type === "strengths_needs_table" && question.domains) {
        const table = {};
        const normalizeDomain = (s) => (s || "").toLowerCase().replace(/[/\s-]+/g, " ").trim();
        // Map impairment A/B/C to label: use question.priorityOptions (e.g. "A — Mild") for ISPP, else Low/Medium/High
        const impairmentToPriority = (v, useQuestionLabels) => {
          const x = String(v || "").toUpperCase().trim();
          if (useQuestionLabels && question.priorityOptions) {
            const opt = question.priorityOptions.find((o) => String(o.value || "").toUpperCase().trim() === x);
            if (opt) return opt.label;
          }
          if (x === "A") return "Low";
          if (x === "B") return "Medium";
          if (x === "C") return "High";
          return String(v || "").trim();
        };
        const isppStyleOptions = question.priorityOptions?.some((o) => /^[ABC]\s*[—-]/.test(String(o.label || "")));
        const getPriorityFromRow = (row) => {
          if (row && typeof row === "object") {
            if (typeof row.A === "boolean" || typeof row.B === "boolean" || typeof row.C === "boolean") {
              if (row.C === true) return impairmentToPriority("C", isppStyleOptions);
              if (row.B === true) return impairmentToPriority("B", isppStyleOptions);
              if (row.A === true) return impairmentToPriority("A", isppStyleOptions);
            }
            const rating = String(row.rating ?? row.Rating ?? "").trim().toUpperCase();
            if (rating === "A" || rating === "B" || rating === "C") return impairmentToPriority(rating, isppStyleOptions);
            // e.g. "B - Moderate", "B — Moderate", "B-Moderate" → take first letter
            const letter = rating.charAt(0);
            if (letter === "A" || letter === "B" || letter === "C") return impairmentToPriority(letter, isppStyleOptions);
          }
          return null;
        };
        const normalizePriorityToLabel = (priority) => {
          if (!priority || typeof priority !== "string") return priority;
          const p = priority.trim();
          if (/^[ABC]$/i.test(p)) return impairmentToPriority(p.toUpperCase(), isppStyleOptions);
          const letter = p.charAt(0).toUpperCase();
          if (letter === "A" || letter === "B" || letter === "C") return impairmentToPriority(letter, isppStyleOptions);
          return priority;
        };

        if (Array.isArray(extractedValue)) {
          // API returns [{ domain, strengths, needs, impairment_rating }, ...]
          extractedValue.forEach((item) => {
            if (!item || typeof item !== "object") return;
            const domainStr = (item.domain || item.Domain || "").trim();
            const normalizedApi = normalizeDomain(domainStr);
            let matched = question.domains.find((d) => {
              const normKey = normalizeDomain(d.key);
              const normName = normalizeDomain(d.name);
              return normalizedApi.includes(normKey) || normalizedApi.includes(normName) || normKey.includes(normalizedApi) || normName.includes(normalizedApi);
            });
            if (!matched && domainStr) {
              // Fuzzy match: "SOCIAL/EMOTIONAL" -> social_emotional, "ACADEMIC/INTELLECTUAL" -> academic, "SELF DETERMINATION" -> behaviour_self_regulation
              if (normalizedApi.includes("social") && normalizedApi.includes("emotional")) matched = question.domains.find((d) => d.key === "social_emotional");
              else if (normalizedApi.includes("communication")) matched = question.domains.find((d) => d.key === "communication");
              else if (normalizedApi.includes("academic") || normalizedApi.includes("intellectual")) matched = question.domains.find((d) => d.key === "academic");
              else if (normalizedApi.includes("behaviour") || normalizedApi.includes("self regulation") || normalizedApi.includes("self determination") || normalizedApi.includes("independence")) matched = question.domains.find((d) => d.key === "behaviour_self_regulation");
              else if (normalizedApi.includes("physical") || normalizedApi.includes("functional")) matched = question.domains.find((d) => d.key === "physical_functional");
            }
            if (!matched) matched = question.domains.find((d) => !table[d.key]);
            if (matched) {
              const strength = scrub(String(item.strengths ?? item.strength ?? item.Strength ?? "").trim());
              const need = scrub(String(item.needs ?? item.need ?? item.Need ?? "").trim());
              if (!strength && !need) return;
              let priority = getPriorityFromRow(item) || String(item.priority ?? item.Priority ?? item.rating ?? item.Rating ?? "").trim();
              if (!priority && (item.impairment_rating || item.ImpairmentRating)) {
                priority = impairmentToPriority(item.impairment_rating ?? item.ImpairmentRating, isppStyleOptions);
              }
              if (!priority && item.impairment_rating) priority = impairmentToPriority(item.impairment_rating, isppStyleOptions);
              if (priority) priority = normalizePriorityToLabel(priority) || priority;
              table[matched.key] = { strength, need, priority: priority || "" };
            }
          });
          // Preserve original domain names for dynamic table (e.g. "Self Determination / Independence")
          if (question.key === "ispp_domain_summary") {
            newIsppDomainSummaryDynamicRows = extractedValue.map((item) => {
              const domainStr = (item.domain || item.Domain || "").trim();
              const strength = scrub(String(item.strengths ?? item.strength ?? item.Strength ?? "").trim());
              const need = scrub(String(item.needs ?? item.need ?? item.Need ?? "").trim());
              let priority = getPriorityFromRow(item) || String(item.priority ?? item.Priority ?? item.rating ?? item.Rating ?? "").trim();
              if (!priority && (item.impairment_rating || item.ImpairmentRating)) priority = impairmentToPriority(item.impairment_rating ?? item.ImpairmentRating, isppStyleOptions);
              if (!priority && item.impairment_rating) priority = impairmentToPriority(item.impairment_rating, isppStyleOptions);
              if (priority) priority = normalizePriorityToLabel(priority) || priority;
              return { domain: domainStr || "—", strength, need, priority: priority || "" };
            });
          }
        } else if (typeof extractedValue === "object") {
          // Object keyed by domain name: { "SOCIAL/EMOTIONAL FUNCTIONING": { STRENGTHS, NEEDS, IMPAIRMENT_RATINGS }, ... }
          const findMatchedDomain = (domainStr) => {
            const normalizedApi = normalizeDomain(domainStr);
            let matched = question.domains.find((d) => {
              const normKey = normalizeDomain(d.key);
              const normName = normalizeDomain(d.name);
              return normalizedApi.includes(normKey) || normalizedApi.includes(normName) || normKey.includes(normalizedApi) || normName.includes(normalizedApi);
            });
            if (!matched && domainStr) {
              if (normalizedApi.includes("social") && normalizedApi.includes("emotional")) matched = question.domains.find((d) => d.key === "social_emotional");
              else if (normalizedApi.includes("communication")) matched = question.domains.find((d) => d.key === "communication");
              else if (normalizedApi.includes("academic") || normalizedApi.includes("intellectual")) matched = question.domains.find((d) => d.key === "academic");
              else if (normalizedApi.includes("behaviour") || normalizedApi.includes("self regulation") || normalizedApi.includes("self determination") || normalizedApi.includes("independence")) matched = question.domains.find((d) => d.key === "behaviour_self_regulation");
              else if (normalizedApi.includes("physical") || normalizedApi.includes("functional")) matched = question.domains.find((d) => d.key === "physical_functional");
            }
            return matched;
          };

          Object.entries(extractedValue).forEach(([domainName, row]) => {
            if (!row || typeof row !== "object") return;
            const matched = findMatchedDomain(domainName);
            if (!matched) return;
            const strength = scrub(String(row.STRENGTHS ?? row.strengths ?? row.strength ?? row.Strength ?? "").trim());
            const need = scrub(String(row.NEEDS ?? row.needs ?? row.need ?? row.Need ?? "").trim());
            if (!strength && !need) return;
            let priority = getPriorityFromRow(row);
            if (!priority) priority = String(row.IMPAIRMENT_RATINGS ?? row.impairment_rating ?? row.rating ?? row.Rating ?? row.priority ?? row.Priority ?? "").trim();
            if (priority) priority = normalizePriorityToLabel(priority) || priority;
            table[matched.key] = { strength, need, priority: priority || "" };
          });

          // Fallback: keys that match d.key or d.name exactly (e.g. "communication")
          question.domains.forEach((d) => {
            if (table[d.key]) return;
            const row = extractedValue[d.key] || extractedValue[d.name];
            if (row && typeof row === "object") {
              const strength = scrub(String(row.STRENGTHS ?? row.strengths ?? row.strength ?? row.Strength ?? "").trim());
              const need = scrub(String(row.NEEDS ?? row.needs ?? row.need ?? row.Need ?? "").trim());
              if (!strength && !need) return;
              let priority = getPriorityFromRow(row);
              if (!priority) priority = String(row.IMPAIRMENT_RATINGS ?? row.impairment_rating ?? row.rating ?? row.Rating ?? row.priority ?? row.Priority ?? "").trim();
              if (priority) priority = normalizePriorityToLabel(priority) || priority;
              table[d.key] = { strength, need, priority: priority || "" };
            } else if (row) {
              const s = scrub(String(row).trim());
              if (s) table[d.key] = { strength: s, need: "", priority: "" };
            }
          });
        } else {
          newAnswers[question.question] = scrub(String(extractedValue));
        }
        if (Object.keys(table).length > 0) {
          newStrengthsNeedsTable[question.key] = table;
          if (question.key === "ispp_domain_summary") {
            isppTableFilledFromPdf = true;
            if (newIsppDomainSummaryDynamicRows === undefined) {
              newIsppDomainSummaryDynamicRows = question.domains
                .filter((d) => table[d.key])
                .map((d) => ({ domain: d.name, ...table[d.key] }));
            }
          }
          const answerStr = question.domains
            .map((d) => {
              const r = table[d.key] || {};
              const s = (r.strength || "").trim();
              const n = (r.need || "").trim();
              const p = (r.priority || "").trim();
              if (!s && !n) return null;
              return `${d.name}: Strength: ${s || ""}, Need: ${n || ""}, Priority: ${p || "—"}`;
            })
            .filter(Boolean)
            .join("; ");
          newAnswers[question.question] = answerStr;
        }
      }
      else if (question.type === "ispp_findings") {
        // Store extracted string; structured form stays empty until user fills it
        newAnswers[question.question] = scrub(String(extractedValue));
      }
      else if (question.type === "category_funding") {
        let categoryStr = "";
        if (typeof extractedValue === "object" && extractedValue !== null) {
          categoryStr = String(extractedValue.designation_category_funding ?? extractedValue.category ?? extractedValue.designation ?? "").trim();
        } else {
          const raw = String(extractedValue).trim();
          // Remove any "Category:" prefix and "Funding Level:" suffix if present
          categoryStr = raw.replace(/^Category:\s*/i, "").replace(/,?\s*Funding Level:\s*.+$/i, "").trim();
        }
        const categoryLabel = mapDesignationToCategoryLabel(categoryStr, question.categoryOptions);
        const displayCategory = categoryLabel || (categoryStr ? (categoryStr.startsWith("Other") ? categoryStr : `Other: ${categoryStr}`) : "");
        newAnswers[question.question] = displayCategory || String(extractedValue);
        if (displayCategory && question.key) {
          const isOther = displayCategory.startsWith("Other");
          newCategoryFundingValues[question.key] = {
            category: isOther ? "Other" : displayCategory,
            categoryOther: isOther && displayCategory.replace(/^Other:?\s*/, "") ? displayCategory.replace(/^Other:?\s*/, "") : ""
          };
        }
      }
      else if (question.type === "text_input" || question.type === "textarea" || question.type === "number") {
        // Direct text/number input; format all dates as MM/DD/YYYY; Student Name: masked only, format RXXXXXXXX (no space)
        const valueStr = String(extractedValue);
        if (question.key === "student_profile_first_last_name") {
          let cleanedName = valueStr.replace(/[^a-zA-Z\s]/g, '').trim();
          // Upload/API may return first and last name separately
          if (!cleanedName) {
            const first = (extractedAnswers["Student First Name"] ?? extractedAnswers["student_first_name"] ?? "").toString().replace(/[^a-zA-Z\s]/g, '').trim();
            const last = (extractedAnswers["Student Last Name"] ?? extractedAnswers["student_last_name"] ?? "").toString().replace(/[^a-zA-Z\s]/g, '').trim();
            cleanedName = [first, last].filter(Boolean).join(' ');
          }
          newAnswers[question.question] = maskStudentName(cleanedName); // Store masked only (RXX SXXXX)
        } else {
          const valueOut = question.inputType === "date" ? formatDateToMMDDYYYY(valueStr) : scrub(valueStr);
          newAnswers[question.question] = valueOut;
        }
      }
      else if (question.type === "date_select") {
        // Handle date (expecting format like "January 2024" or object with month/year)
        if (typeof extractedValue === "object") {
          const month = extractedValue.month || extractedValue.month_name || "";
          const year = extractedValue.year || "";
          if (month && year) {
            newAnswers[question.question] = `${month} ${year}`;
          }
        } else {
          newAnswers[question.question] = String(extractedValue);
        }
      }
    });

    // Update all states
    if (clearFirst) {
      setIsppFindings({});
      setCategoryFundingValues({});
      setIsppDomainSummaryTableType("static");
      setIsppDomainSummaryDynamicRows([]);
    }
    if (isppTableFilledFromPdf) setIsppDomainSummaryTableType("dynamic");
    if (newIsppDomainSummaryDynamicRows !== undefined) setIsppDomainSummaryDynamicRows(newIsppDomainSummaryDynamicRows);
    setAnswers(newAnswers);
    setMultiSelectValues(newMultiSelectValues);
    setOtherTextValues(newOtherTextValues);
    setDomainRatings(newDomainRatings);
    setStrengthsNeedsTable(newStrengthsNeedsTable);
    setSupportEstimatorValues(newSupportEstimatorValues);
    setCategoryFundingValues(newCategoryFundingValues);
  };

  const handleBack = () => {
    if (showUploadStep) {
      sessionStorage.setItem("titleId", "");
      setTitleId("");
      setInitialStart(false);
      setHighLightBtn(false);
      navigate("/");
      return;
    }
    if (currentIndex === section4EndIndex && section4StartIndex >= 0) {
      setCurrentIndex(section4StartIndex);
      return;
    }
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    } else if (currentIndex === 0) {
      setShowUploadStep(true);
    }
  };

  // One-time optional upload step (before any questions). Min-height when processing so layout doesn't jump; new upload disabled until done.
  const uploadStepContent = (
    <div style={{
      marginBottom: "20px",
      padding: "20px",
      backgroundColor: "#f0f7ff",
      borderRadius: "10px",
      border: "2px solid #2c68a1",
      minHeight: isUploadingPDF ? "260px" : undefined
    }}>
      <label style={{
        display: "block",
        fontSize: "16px",
        fontWeight: "600",
        color: "#2c68a1",
        marginBottom: "8px"
      }}>
        Learning Support Documents <span style={{ fontWeight: "400", color: "#666", fontSize: "14px" }}>(Optional)</span>
      </label>
      <p style={{ fontSize: "14px", color: "#666", margin: "0 0 10px 0" }}>
        Upload supporting documents to automatically pre-fill the questionnaire.
      </p>
      <p style={{ fontSize: "14px", color: "#666", margin: "0 0 6px 0", fontWeight: "600" }}>Examples:</p>
      <ul style={{ fontSize: "14px", color: "#666", margin: "0 0 12px 0", paddingLeft: "20px" }}>
        <li>Student Support Plan</li>
        <li>Student CB IEP Survey</li>
        <li>Previous ISPP Forms</li>
      </ul>
      <p style={{ fontSize: "14px", color: "#666", margin: "0 0 6px 0" }}>
        Supported file types: PDF, DOC, DOCX, JPG, JPEG, and PNG.
      </p>
      <p style={{ fontSize: "14px", color: "#666", margin: "0 0 6px 0" }}>
        Maximum total upload size: {MAX_FILE_SIZE_MB} MB.
      </p>
      <p style={{ fontSize: "14px", color: "#666", margin: "0 0 12px 0" }}>
        <b>You may skip this step and complete the questionnaire manually.</b>
      </p>
      {/* Top: list of attached files (jo bhi attachment add hoga wo yahan top pe aa jayega) */}
      {attachedFiles.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <strong style={{ fontSize: "13px", color: "#333" }}>Attached ({attachedFiles.length}):</strong>
          <ul style={{ margin: "6px 0 0 0", paddingLeft: "0", listStyle: "none" }}>
            {attachedFiles.map((file, i) => (
              <li
                key={`${file.name}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  marginTop: "4px",
                  backgroundColor: "#fff",
                  borderRadius: "6px",
                  border: "1px solid #ddd"
                }}
              >
                <span style={{ fontSize: "13px", color: "#333", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }} title={uploadedFileNames[i] || file.name}>
                  {uploadedFileNames[i] || file.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  style={{
                    marginLeft: "8px",
                    padding: "4px 10px",
                    backgroundColor: "transparent",
                    color: "#c9302c",
                    border: "1px solid #c9302c",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                  title="Remove"
                >
                  <i className="fa fa-times" style={{ marginRight: "4px" }} /> Remove
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={clearAllAttachments}
            style={{
              marginTop: "8px",
              padding: "4px 12px",
              backgroundColor: "transparent",
              color: "#666",
              border: "1px solid #999",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            Clear all attachments
          </button>
        </div>
      )}
      {uploadedFileNames.length > 0 && (
        <div style={{ fontSize: "12px", color: "#28a745", marginBottom: "16px" }}>
          <strong>✓ Processed ({uploadedFileNames.length}):</strong>
          <ul style={{ margin: "4px 0 0 0", paddingLeft: "18px" }}>
            {uploadedFileNames.map((name, i) => (
              <li key={i}>{name}</li>
            ))}
          </ul>
        </div>
      )}
      {isUploadingPDF && (
        <div style={{
          fontSize: "15px",
          fontWeight: "600",
          color: "#2c68a1",
          marginBottom: "12px",
          padding: "10px 14px",
          backgroundColor: "#e8f4fc",
          borderRadius: "8px",
          display: "inline-flex",
          alignItems: "center"
        }}>
          <i className="fa fa-spinner fa-spin" style={{ marginRight: "10px", fontSize: "18px" }} />
          Processing document(s)…
        </div>
      )}
      {/* Upload actions + Start Questionnaire inside the box */}
      <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginTop: "12px" }}>
        <input
          ref={attachmentInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/jpeg,image/png"
          multiple
          onChange={handleAttachmentAdd}
          style={{ display: "none" }}
          id="attachment-upload-input"
        />
        <label
          htmlFor="attachment-upload-input"
          style={{
            padding: "10px 20px",
            backgroundColor: "#2c68a1",
            color: "white",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            display: "inline-block"
          }}
        >
          <i className="fa fa-upload" style={{ marginRight: "8px" }} />
          Upload Documents
        </label>
        <button
          type="button"
          disabled={isUploadingPDF}
          onClick={() => setShowUploadStep(false)}
          style={{
            padding: "10px 24px",
            backgroundColor: isUploadingPDF ? "#999" : "rgba(44, 104, 161, 1)",
            color: "white",
            border: `2px solid ${isUploadingPDF ? "#999" : "rgba(44, 104, 161, 1)"}`,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: isUploadingPDF ? "not-allowed" : "pointer"
          }}
        >
          Start Questionnaire
        </button>
      </div>
    </div>
  );

  if (showUploadStep) {
    return (
      <div className={`${addClass ? "multipleChoice shrink" : ""} home-inner-page content`} id="content">
        <div className="homme-outer-containers" style={{ width: "100%", maxWidth: "100%" }}>
          <div className="content-innerss scsbc-chartboart learning-supports-full-width">
            {uploadStepContent}
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div>Loading...</div>;
  }

  return (
    <div className={`${addClass ? "multipleChoice shrink" : ""} home-inner-page content`} id="content">
      <div className="homme-outer-containers" style={{ width: "100%", maxWidth: "100%" }}>
        <div className={`content-innerss scsbc-chartboart learning-supports-full-width${currentQuestion?.key === "ispp_domain_summary" ? " chart-boart-support-inn" : ""}`}>
          {isShowingSection4Block ? (
            <>
              <h6 style={{ marginBottom: "20px", fontSize: "16px", fontWeight: "600" }}>
                {section4StartIndex + 1}. Student Details
              </h6>
              <p style={{ marginBottom: "16px", fontSize: "14px", color: "#555", fontWeight: "600" }}>
                Required fields are marked with *.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {section4QuestionsSorted.map((q, idx) => {
                  const qAnswer = answers[q.question] || "";
                  return (
                    <div key={q.key || idx} style={{
                      padding: "16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "10px",
                      backgroundColor: "#fafafa"
                    }}>
                      <div style={{ marginBottom: "12px", fontSize: "15px", fontWeight: "600", color: "#333" }}>
                        {q.question}
                        {isQuestionRequired(q) && (
                          <span style={{ color: "red", fontWeight: "bold" }}> *</span>
                        )}
                      </div>
                      <div className="edit-content-answer">
                        {q.helperText && (
                          <p style={QUESTIONNAIRE_HELPER_TEXT_STYLE}>
                            {q.helperText}
                          </p>
                        )}
                        {q.type === "select" && q.options ? (
                          <select
                            value={qAnswer}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.question]: e.target.value }))}
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              fontSize: "16px",
                              borderRadius: "10px",
                              border: "1px solid #ccc",
                              backgroundColor: "#fff"
                            }}
                          >
                            <option value="">{q.key === "student_profile_grade" ? "Select a grade" : "Select an option"}</option>
                            {q.options.map((opt, i) => (
                              <option key={i} value={opt.label}>{opt.label}</option>
                            ))}
                          </select>
                        ) : q.type === "radio" && q.options ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {q.options.map((opt, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <input
                                  type="radio"
                                  name={q.key}
                                  checked={qAnswer === opt.label}
                                  onChange={() => setAnswers((prev) => ({ ...prev, [q.question]: opt.label }))}
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    cursor: "pointer",
                                    accentColor: "#2c68a1"
                                  }}
                                />
                                <label style={{ fontSize: "16px", cursor: "pointer", flex: 1 }}>
                                  {opt.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : q.type === "checkbox" && q.options ? (
                          q.key === "student_profile_support_team_members" ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {(qAnswer || "")
                                  .split(" | ")
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                                  .map((tag, i) => (
                                    <span
                                      key={`${tag}-${i}`}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        backgroundColor: "#e8f1fb",
                                        color: "#2c68a1",
                                        border: "1px solid #b7d0eb",
                                        borderRadius: "16px",
                                        padding: "6px 10px",
                                        fontSize: "13px",
                                        fontWeight: "600"
                                      }}
                                    >
                                      {tag}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const nextSelections = (qAnswer || "")
                                            .split(" | ")
                                            .map((s) => s.trim())
                                            .filter(Boolean)
                                            .filter((item) => item !== tag);
                                          setAnswers((prev) => ({ ...prev, [q.question]: nextSelections.join(" | ") }));
                                        }}
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          color: "#2c68a1",
                                          cursor: "pointer",
                                          fontSize: "14px",
                                          lineHeight: 1
                                        }}
                                        aria-label={`Remove ${tag}`}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {q.options.map((opt, i) => {
                                  const selections = (qAnswer || "")
                                    .split(" | ")
                                    .map((s) => s.trim())
                                    .filter(Boolean);
                                  const isSelected = selections.includes(opt.label);
                                  return (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => {
                                        const currentSelections = (qAnswer || "")
                                          .split(" | ")
                                          .map((s) => s.trim())
                                          .filter(Boolean);
                                        const nextSelections = isSelected
                                          ? currentSelections.filter((item) => item !== opt.label)
                                          : [...currentSelections, opt.label];
                                        setAnswers((prev) => ({ ...prev, [q.question]: nextSelections.join(" | ") }));
                                      }}
                                      style={{
                                        padding: "6px 10px",
                                        borderRadius: "16px",
                                        border: isSelected ? "1px solid #2c68a1" : "1px solid #cfd8e3",
                                        backgroundColor: isSelected ? "#2c68a1" : "#fff",
                                        color: isSelected ? "#fff" : "#333",
                                        fontSize: "13px",
                                        cursor: "pointer"
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <div style={{ display: "flex", gap: "8px" }}>
                                <input
                                  type="text"
                                  value={otherTextValues[`${q.key}_tag_input`] || ""}
                                  onChange={(e) =>
                                    setOtherTextValues((prev) => ({ ...prev, [`${q.key}_tag_input`]: e.target.value }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key !== "Enter" && e.key !== ",") return;
                                    e.preventDefault();
                                    const raw = (otherTextValues[`${q.key}_tag_input`] || "").trim();
                                    if (!raw) return;
                                    const currentSelections = (qAnswer || "")
                                      .split(" | ")
                                      .map((s) => s.trim())
                                      .filter(Boolean);
                                    if (!currentSelections.includes(raw)) {
                                      setAnswers((prev) => ({
                                        ...prev,
                                        [q.question]: [...currentSelections, raw].join(" | ")
                                      }));
                                    }
                                    setOtherTextValues((prev) => ({ ...prev, [`${q.key}_tag_input`]: "" }));
                                  }}
                                  placeholder={q.otherPlaceholder || "Type role/person and press Enter"}
                                  style={{
                                    flex: 1,
                                    padding: "10px 14px",
                                    fontSize: "16px",
                                    borderRadius: "10px",
                                    border: "1px solid #ccc",
                                    backgroundColor: "#fff"
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const raw = (otherTextValues[`${q.key}_tag_input`] || "").trim();
                                    if (!raw) return;
                                    const currentSelections = (qAnswer || "")
                                      .split(" | ")
                                      .map((s) => s.trim())
                                      .filter(Boolean);
                                    if (!currentSelections.includes(raw)) {
                                      setAnswers((prev) => ({
                                        ...prev,
                                        [q.question]: [...currentSelections, raw].join(" | ")
                                      }));
                                    }
                                    setOtherTextValues((prev) => ({ ...prev, [`${q.key}_tag_input`]: "" }));
                                  }}
                                  style={{
                                    padding: "10px 14px",
                                    borderRadius: "10px",
                                    border: "1px solid #2c68a1",
                                    backgroundColor: "#2c68a1",
                                    color: "#fff",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                  }}
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                              {q.options
                                .filter((opt) => !(q.allowOther && opt.value === "other"))
                                .map((opt, i) => {
                                  const selections = (qAnswer || "")
                                    .split(" | ")
                                    .map((s) => s.trim())
                                    .filter(Boolean);
                                  const isChecked = selections.includes(opt.label);
                                  return (
                                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {
                                          const currentSelections = (qAnswer || "")
                                            .split(" | ")
                                            .map((s) => s.trim())
                                            .filter(Boolean);
                                          const nextSelections = isChecked
                                            ? currentSelections.filter((item) => item !== opt.label)
                                            : [...currentSelections, opt.label];
                                          setAnswers((prev) => ({
                                            ...prev,
                                            [q.question]: nextSelections.join(" | ")
                                          }));
                                        }}
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          cursor: "pointer",
                                          accentColor: "#2c68a1"
                                        }}
                                      />
                                      <label style={{ fontSize: "16px", cursor: "pointer", flex: 1 }}>
                                        {opt.label}
                                      </label>
                                    </div>
                                  );
                                })}
                            </div>
                          )
                        ) : q.type === "number" ? (
                          <input
                            type="number"
                            value={qAnswer}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.question]: e.target.value }))}
                            placeholder={q.placeholder || "Enter a number"}
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              fontSize: "16px",
                              borderRadius: "10px",
                              border: "1px solid #ccc",
                              backgroundColor: "#fff"
                            }}
                          />
                        ) : q.type === "textarea" ? (
                          <>
                            {AI_GENERATE_QUESTION_KEYS.has(q.key) && (
                              <>
                                <div style={{
                                  fontSize: "13px",
                                  color: "#2c68a1",
                                  marginBottom: "8px",
                                  fontWeight: "600"
                                }}>
                                  🤖 AI will generate this content
                                </div>
                                <button
                                  type="button"
                                  disabled={generatingAIForKeys.includes(q.key)}
                                  onClick={() => handleGenerateAIForSection4(q)}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "10px 20px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#fff",
                                    backgroundColor: generatingAIForKeys.includes(q.key) ? "#999" : "#2c68a1",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: generatingAIForKeys.includes(q.key) ? "not-allowed" : "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "8px"
                                  }}
                                >
                                  {generatingAIForKeys.includes(q.key) && (
                                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "16px" }} aria-hidden="true" />
                                  )}
                                  {generatingAIForKeys.includes(q.key) ? "Generating…" : "Generate with AI"}
                                </button>
                              </>
                            )}
                            <TextareaAutosize
                              minRows={3}
                              maxRows={10}
                              style={{ width: "100%", resize: "none", borderRadius: "8px", border: "1px solid #ccc",padding:"10px 14px" }}
                              value={qAnswer}
                              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.question]: e.target.value }))}
                              placeholder={q.placeholder || "Write your answer..."}
                            />
                          </>
                        ) : q.type === "text_input" ? (
                          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                            <input
                              type={q.inputType === "date" ? "date" : "text"}
                              value={q.inputType === "date" ? toDateInputValue(qAnswer) : qAnswer}
                              onChange={(e) => {
                                let val = e.target.value;
                                if (q.key === "student_profile_first_last_name") {
                                  // Allow typing with masking
                                  const filteredValue = val.replace(/[^a-zA-Z\s]/g, '');
                                  setAnswers((prev) => ({ ...prev, [q.question]: maskStudentName(filteredValue) }));
                                  return;
                                }
                                else if (q.inputType === "date") val = formatDateToMMDDYYYY(e.target.value);
                                else val = e.target.value;
                                setAnswers((prev) => ({ ...prev, [q.question]: val }));
                              }}
                              placeholder={q.placeholder || (q.inputType === "date" ? "Select date" : "Enter text")}
                              style={{
                                width: "100%",
                                padding: "10px 14px",
                                paddingRight: q.key === "student_profile_first_last_name" && qAnswer ? "40px" : "14px",
                                fontSize: "16px",
                                backgroundColor: "#fff",
                                borderRadius: "10px",
                                border: "1px solid #ccc"
                              }}
                            />
                            {q.key === "student_profile_first_last_name" && qAnswer && (
                              <button
                                type="button"
                                onClick={() => {
                                  setAnswers((prev) => ({ ...prev, [q.question]: '' }));
                                }}
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  color: "#999",
                                  padding: "0",
                                  width: "24px",
                                  height: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                                title="Clear name"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="button-next" style={{ marginTop: "24px" }}>
                <button
                  style={{
                    backgroundColor: "rgba(44, 104, 161, 1)",
                    border: "2px solid rgba(44, 104, 161, 1)",
                    borderRadius: "12px",
                    cursor: "pointer",
                    marginRight: "10px",
                    fontWeight: "600"
                  }}
                  onClick={handleBack}
                >
                  <i className="fa fa-arrow-circle-left" aria-hidden="true" style={{ marginRight: "8px", marginLeft: "0" }} />
                  Back
                </button>
                <button
                  style={{ backgroundColor: "rgba(44, 104, 161, 1)", flex: "1" }}
                  onClick={() => handleNext("true")}
                >
                  Next <i className="fa fa-arrow-circle-right" aria-hidden="true" />
                </button>
              </div>
            </>
          ) : (
            <>
          {currentQuestion?.section && currentQuestion?.displaySection !== false && (
            <div style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#2c68a1",
              marginBottom: "15px",
              paddingBottom: "10px",
              borderBottom: "2px solid #2c68a1"
            }}>
              {currentQuestion.section}
            </div>
          )}
          <h6>
            {displayQuestionNumber}.{" "}
            <span style={{ whiteSpace: "pre-line" }}>{currentQuestion?.question}</span>
            {isQuestionRequired(currentQuestion) && (
              <span style={{ color: "red", fontWeight: "bold" }}> *</span>
            )}
          </h6>

          <div className="edit-content-answer">
            {currentQuestion?.type === "info" ? (
              <div style={{
                padding: "20px",
                backgroundColor: "#f0f7ff",
                borderRadius: "10px",
                border: "1px solid #2c68a1",
                marginTop: "10px"
              }}>
                <p style={{ margin: "0 0 12px 0", fontSize: "15px", color: "#333" }}>
                  AI will automatically generate the following after you complete the form:
                </p>
                <ul style={{
                  margin: 0,
                  paddingLeft: "20px",
                  fontSize: "15px",
                  lineHeight: 1.6,
                  color: "#444"
                }}>
                  {(currentQuestion?.content && Array.isArray(currentQuestion.content))
                    ? currentQuestion.content.map((item, i) => (
                        <li key={i} style={{ marginBottom: "8px" }}>{item}</li>
                      ))
                    : null}
                </ul>
              </div>
            ) : currentQuestion?.type === "ispp_findings" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginTop: "12px" }}>
                {/* <p style={{ fontSize: "14px", color: "#555", marginBottom: "8px", fontWeight: "600" }}>
                  Suggested structure
                </p> */}
                {/* 1️⃣ Primary Domains Assessed — checkboxes */}
                <div style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                  <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#2c68a1" }}>
                    Primary Domains Assessed
                  </div>
                  {/* <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>✔️ Checkboxes (multiple allowed)</p> */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {(currentQuestion.primaryDomainsOptions || []).map((opt) => {
                      const data = isppFindings[currentQuestion?.key] || {};
                      const selected = (data.primaryDomains || []).includes(opt.label);
                      return (
                        <div key={opt.value} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <input
                            type="checkbox"
                            id={`ispp_domain_${currentQuestion?.key}_${opt.value}`}
                            checked={selected}
                            onChange={() => handleIsppPrimaryDomainToggle(opt.label)}
                            style={{ width: "20px", height: "20px", cursor: "pointer", accentColor: "#2c68a1" }}
                          />
                          <label htmlFor={`ispp_domain_${currentQuestion?.key}_${opt.value}`} style={{ fontSize: "15px", cursor: "pointer", userSelect: "none" }}>
                            {opt.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* 2️⃣ Impairment Rating — radio */}
                <div style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                  <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#2c68a1" }}>
                   Impairment Rating
                  </div>
                  {/* <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>✔️ Radio buttons (single choice)</p> */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {(currentQuestion.impairmentRatingOptions || []).map((opt) => {
                      const data = isppFindings[currentQuestion?.key] || {};
                      const selected = data.impairmentRating === opt.label;
                      return (
                        <div key={opt.value} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <input
                            type="radio"
                            name={`ispp_rating_${currentQuestion?.key}`}
                            checked={selected}
                            onChange={() => handleIsppImpairmentRating(opt.label)}
                            style={{ width: "20px", height: "20px", cursor: "pointer", accentColor: "#2c68a1" }}
                          />
                          <label style={{ fontSize: "15px", cursor: "pointer", userSelect: "none" }}>{opt.label}</label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* 3️⃣ Key Strengths — textarea */}
                <div style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                  <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#2c68a1" }}>
                   Key Strengths
                  </div>
                  {/* <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>✔️ Textarea / long input (Free text – anonymized)</p> */}
                  <TextareaAutosize
                    minRows={3}
                    maxRows={8}
                    style={{ width: "100%", resize: "none", borderRadius: "8px", border: "1px solid #ccc", padding: "10px 14px" }}
                    value={(isppFindings[currentQuestion?.key] || {}).keyStrengths || ""}
                    onChange={(e) => handleIsppTextChange("keyStrengths", e.target.value)}
                    placeholder={currentQuestion.keyStrengthsPlaceholder || "Free text – anonymized"}
                  />
                </div>
                {/* 4️⃣ Primary Needs — textarea */}
                <div style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                  <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#2c68a1" }}>
                    Primary Needs
                  </div>
                  {/* <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>✔️ Textarea / long input</p> */}
                  <TextareaAutosize
                    minRows={3}
                    maxRows={8}
                    style={{ width: "100%", resize: "none", borderRadius: "8px", border: "1px solid #ccc", padding: "10px 14px" }}
                    value={(isppFindings[currentQuestion?.key] || {}).primaryNeeds || ""}
                    onChange={(e) => handleIsppTextChange("primaryNeeds", e.target.value)}
                    placeholder={currentQuestion.primaryNeedsPlaceholder || "Describe primary needs"}
                  />
                </div>
                {/* 5️⃣ Recommended Strategies / Supports — textarea */}
                <div style={{ padding: "16px", border: "1px solid #e0e0e0", borderRadius: "10px", backgroundColor: "#fafafa" }}>
                  <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#2c68a1" }}>
                    Recommended Strategies / Supports
                  </div>
                  {/* <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>✔️ Textarea / long input</p> */}
                  <TextareaAutosize
                    minRows={3}
                    maxRows={8}
                    style={{ width: "100%", resize: "none", borderRadius: "8px", border: "1px solid #ccc", padding: "10px 14px" }}
                    value={(isppFindings[currentQuestion?.key] || {}).recommendedStrategies || ""}
                    onChange={(e) => handleIsppTextChange("recommendedStrategies", e.target.value)}
                    placeholder={currentQuestion.recommendedStrategiesPlaceholder || "Describe recommended strategies / supports"}
                  />
                </div>
              </div>
            ) : currentQuestion?.type === "radio" && currentQuestion?.options ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {currentQuestion?.helperText && (
                  <p style={QUESTIONNAIRE_HELPER_TEXT_STYLE}>
                    {currentQuestion.helperText}
                  </p>
                )}
                {currentQuestion.options.map((opt, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <input
                      type="radio"
                      name={currentQuestion.key}
                      checked={opt.value === "other" 
                        ? currentAnswer.startsWith("Other:") || currentAnswer === "Other"
                        : currentAnswer === opt.label}
                      onChange={() => {
                        if (opt.value === "other") {
                          handleRadioChange(opt.value, "Other");
                        } else {
                          handleRadioChange(opt.value, opt.label);
                        }
                      }}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        accentColor: "#2c68a1"
                      }}
                    />
                    <label style={{ fontSize: "16px", cursor: "pointer", flex: 1 }}>
                      {opt.label === "Other" ? "Other:" : opt.label}
                    </label>
                  </div>
                ))}
                {currentQuestion.allowOther && 
                 (currentAnswer === "Other" || currentAnswer.startsWith("Other:")) && (
                  <input
                    type="text"
                    value={
                      otherTextValues[currentQuestion.key] !== undefined && otherTextValues[currentQuestion.key] !== null
                        ? otherTextValues[currentQuestion.key]
                        : (currentAnswer.startsWith("Other:") ? currentAnswer.replace(/^Other:\s*/, "") : "")
                    }
                    onChange={(e) => {
                      const otherText = e.target.value;
                      setOtherTextValues(prev => ({ ...prev, [currentQuestion.key]: otherText }));
                      setAnswers(prev => ({ ...prev, [currentQuestion?.question]: otherText ? `Other: ${otherText}` : "Other" }));
                    }}
                    placeholder={currentQuestion.otherPlaceholder || "Please specify"}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #ccc",
                      marginLeft: "30px"
                    }}
                  />
                )}
                {currentQuestion?.key === "instructional_support_check" && currentAnswer === "No" && (
                  <div style={{
                    marginTop: "20px",
                    padding: "16px",
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "10px"
                  }}>
                    <p style={{ margin: "0 0 14px 0", fontSize: "15px", color: "#856404", lineHeight: 1.5 }}>
                      Based on BC Ministry guidelines, this student does not currently require an IEP. You may continue with classroom support planning or return to update designation status if circumstances change.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setTitleId("");
                        setInitialStart(false);
                        setHighLightBtn(false);
                        navigate("/");
                      }}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#2c68a1",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <i className="fa fa-home" aria-hidden="true" />
                      Home
                    </button>
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "checkbox" && currentQuestion?.options ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {currentQuestion?.helperText && (
                  <p style={QUESTIONNAIRE_HELPER_TEXT_STYLE}>
                    {currentQuestion.helperText}
                  </p>
                )}
                {/* Filter out "other" option if allowOther is true - it will be shown separately */}
                {currentQuestion.options
                  .filter(opt => !(currentQuestion.allowOther && opt.value === "other"))
                  .map((opt) => (
                    <div
                      key={opt.value}
                      role="button"
                      tabIndex={0}
                      onClick={handleCheckboxRowClick(opt.value, opt.label)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCheckboxToggle(opt.value, opt.label); } }}
                      style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "6px 0" }}
                    >
                      <input
                        type="checkbox"
                        id={`checkbox_${currentQuestion.key}_${opt.value}`}
                        checked={isOptionSelected(opt.value)}
                        onChange={() => handleCheckboxToggle(opt.value, opt.label)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: "20px",
                          height: "20px",
                          minWidth: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label
                        htmlFor={`checkbox_${currentQuestion.key}_${opt.value}`}
                        style={{ fontSize: "16px", cursor: "pointer", flex: 1, userSelect: "none", margin: 0 }}
                        onClick={(e) => e.preventDefault()}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                {currentQuestion.allowOther && (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                      <input
                        type="checkbox"
                        id={`checkbox_${currentQuestion.key}_other`}
                        checked={isOptionSelected("other")}
                        onChange={() => handleCheckboxToggle("other", "Other")}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`checkbox_${currentQuestion.key}_other`}
                        style={{ fontSize: "16px", cursor: "pointer", userSelect: "none" }}
                      >
                        Other:
                      </label>
                    </div>
                    {isOptionSelected("other") && (
                      <input
                        type="text"
                        value={otherTextValues[currentQuestion.key] || ""}
                        onChange={(e) => handleOtherTextChange(currentQuestion.key, e.target.value)}
                        placeholder={currentQuestion.otherPlaceholder || "Please specify"}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "1px solid #ccc",
                          marginLeft: "30px"
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "multi_select" && currentQuestion?.options ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Filter out "other" option if allowOther is true - it will be shown separately */}
                {currentQuestion.options
                  .filter(opt => !(currentQuestion.allowOther && opt.value === "other"))
                  .map((opt, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <input
                        type="checkbox"
                        id={`multiselect_${currentQuestion.key}_${i}`}
                        checked={isOptionSelected(opt.value)}
                        onChange={() => handleCheckboxToggle(opt.value, opt.label)}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`multiselect_${currentQuestion.key}_${i}`}
                        style={{ fontSize: "16px", cursor: "pointer", flex: 1, userSelect: "none" }}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                {currentQuestion.allowOther && (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                      <input
                        type="checkbox"
                        id={`multiselect_${currentQuestion.key}_other`}
                        checked={isOptionSelected("other")}
                        onChange={() => handleCheckboxToggle("other", "Other")}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`multiselect_${currentQuestion.key}_other`}
                        style={{ fontSize: "16px", cursor: "pointer", userSelect: "none" }}
                      >
                        Other:
                      </label>
                    </div>
                    {isOptionSelected("other") && (
                      <input
                        type="text"
                        value={otherTextValues[currentQuestion.key] || ""}
                        onChange={(e) => handleOtherTextChange(currentQuestion.key, e.target.value)}
                        placeholder={currentQuestion.otherPlaceholder || "Please specify"}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "1px solid #ccc",
                          marginLeft: "30px"
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "domain_ratings" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {currentQuestion.domains && Array.isArray(currentQuestion.domains) && currentQuestion.domains.length > 0 ? (
                  currentQuestion.domains.map((domain, i) => {
                    const ratingOptions = currentQuestion.ratingOptions || [];
                    return (
                      <div key={i} style={{
                        padding: "15px",
                        border: "1px solid #ddd",
                        borderRadius: "10px",
                        backgroundColor: "#f9f9f9"
                      }}>
                        <div style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "12px",
                          color: "#2c68a1"
                        }}>
                          {domain.name}
                        </div>
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                          {ratingOptions.length > 0 ? (
                            ratingOptions.map((rating, j) => (
                              <div key={j} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <input
                                  type="radio"
                                  name={`domain_${domain.key}`}
                                  checked={getDomainRating(domain.key) === rating.value}
                                  onChange={() => handleDomainRatingChange(domain.key, rating.value, rating.label)}
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: "pointer",
                                    accentColor: "#2c68a1"
                                  }}
                                />
                                <label style={{ fontSize: "15px", cursor: "pointer" }}>
                                  {rating.label}
                                </label>
                              </div>
                            ))
                          ) : (
                            <div style={{ color: "#856404", padding: "10px", fontStyle: "italic" }}>
                              Rating options not configured
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ 
                    color: "#856404", 
                    padding: "15px", 
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "10px"
                  }}>
                    <strong>Configuration Issue:</strong> Domain ratings question is missing domain definitions.
                    {currentQuestion && (
                      <div style={{ marginTop: "10px", fontSize: "14px" }}>
                        Question key: {currentQuestion.key}<br/>
                        Has domains: {currentQuestion.domains ? "Yes" : "No"}<br/>
                        Has ratingOptions: {currentQuestion.ratingOptions ? "Yes" : "No"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "strengths_needs_table" && currentQuestion?.domains ? (
              <div style={{ marginTop: "10px" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden", tableLayout: "fixed" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#2c68a1", color: "#fff" }}>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ddd", width: "22%" }}>Domain</th>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ddd", width: "30%" }}>Strengths</th>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ddd", width: "30%" }}>Needs</th>
                        <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ddd", width: "18%" }}>{currentQuestion.priorityColumnLabel || "Priority"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentQuestion.key === "ispp_domain_summary" && isppDomainSummaryTableType === "dynamic" && isppDomainSummaryDynamicRows.length > 0
                        ? isppDomainSummaryDynamicRows.map((row, index) => {
                            const priorityOptions = currentQuestion.priorityOptions || [
                              { value: "high", label: "High" },
                              { value: "medium", label: "Medium" },
                              { value: "low", label: "Low" }
                            ];
                            return (
                              <tr key={`dynamic-${index}-${row.domain}`} style={{ backgroundColor: "#f9f9f9", borderBottom: "1px solid #eee", verticalAlign: "top" }}>
                                <td style={{ padding: "10px", fontWeight: "600", color: "#2c68a1", borderRight: "1px solid #eee" }}>{row.domain || "—"}</td>
                                <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                                  <textarea
                                    value={row.strength || ""}
                                    onChange={(e) => handleIsppDomainSummaryDynamicChange(index, "strength", e.target.value)}
                                    placeholder="List strengths for this domain"
                                    rows={4}
                                    style={{ width: "100%", minHeight: "80px", padding: "8px 10px", fontSize: "14px", borderRadius: "6px", border: "1px solid #ccc", resize: "vertical", boxSizing: "border-box" }}
                                  />
                                </td>
                                <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                                  <textarea
                                    value={row.need || ""}
                                    onChange={(e) => handleIsppDomainSummaryDynamicChange(index, "need", e.target.value)}
                                    placeholder="List needs for this domain"
                                    rows={4}
                                    style={{ width: "100%", minHeight: "80px", padding: "8px 10px", fontSize: "14px", borderRadius: "6px", border: "1px solid #ccc", resize: "vertical", boxSizing: "border-box" }}
                                  />
                                </td>
                                <td style={{ padding: "8px", borderRight: "1px solid #eee", overflow: "visible", position: "relative" }}>
                                  {currentQuestion.priorityColumnLabel === "A, B, C" ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                      {priorityOptions.map((opt) => {
                                        const p = (row.priority || "").trim();
                                        const isChecked = (o) => (o.label || "").trim() === p;
                                        const checked = isChecked(opt);
                                        return (
                                          <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => handleIsppDomainSummaryDynamicChange(index, "priority", checked ? "" : opt.label)}
                                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                            />
                                            <span>{opt.value}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <select
                                      value={(() => {
                                        const p = (row.priority || "").trim();
                                        const hasMatch = priorityOptions.some((o) => (o.label || "").trim() === p);
                                        return hasMatch ? p : "";
                                      })()}
                                      onChange={(e) => handleIsppDomainSummaryDynamicChange(index, "priority", e.target.value)}
                                      style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        fontSize: "14px",
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                      }}
                                    >
                                      <option value="">Select</option>
                                      {priorityOptions.map((opt) => (
                                        <option key={opt.value} value={opt.label}>{opt.label}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        : currentQuestion.domains.map((domain) => {
                            const row = strengthsNeedsTable[currentQuestion.key]?.[domain.key] || {};
                            const priorityOptions = currentQuestion.priorityOptions || [
                              { value: "high", label: "High" },
                              { value: "medium", label: "Medium" },
                              { value: "low", label: "Low" }
                            ];
                            return (
                              <tr key={domain.key} style={{ backgroundColor: "#f9f9f9", borderBottom: "1px solid #eee", verticalAlign: "top" }}>
                                <td style={{ padding: "10px", fontWeight: "600", color: "#2c68a1", borderRight: "1px solid #eee" }}>{domain.name}</td>
                                <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                                  <textarea
                                    value={row.strength || ""}
                                    onChange={(e) => handleStrengthsNeedsTableChange(currentQuestion.key, domain.key, "strength", e.target.value)}
                                    placeholder="List strengths for this domain"
                                    rows={4}
                                    style={{ width: "100%", minHeight: "80px", padding: "8px 10px", fontSize: "14px", borderRadius: "6px", border: "1px solid #ccc", resize: "vertical", boxSizing: "border-box" }}
                                  />
                                </td>
                                <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                                  <textarea
                                    value={row.need || ""}
                                    onChange={(e) => handleStrengthsNeedsTableChange(currentQuestion.key, domain.key, "need", e.target.value)}
                                    placeholder="List needs for this domain"
                                    rows={4}
                                    style={{ width: "100%", minHeight: "80px", padding: "8px 10px", fontSize: "14px", borderRadius: "6px", border: "1px solid #ccc", resize: "vertical", boxSizing: "border-box" }}
                                  />
                                </td>
                                <td style={{ padding: "8px", borderRight: "1px solid #eee", overflow: "visible", position: "relative" }}>
                                  {currentQuestion.priorityColumnLabel === "A, B, C" ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                      {priorityOptions.map((opt) => {
                                        const p = (row.priority || "").trim();
                                        const isChecked = (o) => (o.label || "").trim() === p;
                                        const checked = isChecked(opt);
                                        return (
                                          <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => handleStrengthsNeedsTableChange(currentQuestion.key, domain.key, "priority", checked ? "" : opt.label)}
                                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                            />
                                            <span>{opt.value}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <select
                                      key={`${currentQuestion.key}-${domain.key}-priority`}
                                      value={(() => {
                                        const p = (row.priority || "").trim();
                                        const hasMatch = priorityOptions.some((o) => (o.label || "").trim() === p);
                                        return hasMatch ? p : "";
                                      })()}
                                      onChange={(e) => handleStrengthsNeedsTableChange(currentQuestion.key, domain.key, "priority", e.target.value)}
                                      style={{
                                        width: "100%",
                                        padding: "8px 10px",
                                        fontSize: "14px",
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                      }}
                                    >
                                      <option value="">Select</option>
                                      {priorityOptions.map((opt) => (
                                        <option key={opt.value} value={opt.label}>{opt.label}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : currentQuestion?.type === "number" ? (
              <input
                type="number"
                value={currentAnswer}
                onChange={handleNumberInputChange}
                onKeyDown={(e) => {
                  // Prevent 'e', 'E', '+', '-' keys for student age
                  if (currentQuestion?.key === "student_age") {
                    if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }
                }}
                min={currentQuestion?.key === "student_age" ? "0" : undefined}
                placeholder={currentQuestion.placeholder || "Enter a number"}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #ccc",
                  backgroundColor: "#fff"
                }}
              />
            ) : currentQuestion?.type === "text_input" ? (
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  type={currentQuestion?.inputType === "date" ? "date" : "text"}
                  value={currentQuestion?.inputType === "date" ? toDateInputValue(currentAnswer) : currentAnswer}
                  onChange={currentQuestion?.inputType === "date"
                    ? (e) => setAnswers((prev) => ({ ...prev, [currentQuestion.question]: formatDateToMMDDYYYY(e.target.value) }))
                    : handleTextInputChange}
                  placeholder={currentQuestion.placeholder || (currentQuestion?.inputType === "date" ? "Select date" : "Enter text")}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    paddingRight: currentQuestion?.key === "student_profile_first_last_name" && currentAnswer ? "40px" : "14px",
                    fontSize: "16px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff"
                  }}
                />
                {currentQuestion?.key === "student_profile_first_last_name" && currentAnswer && (
                  <button
                    type="button"
                    onClick={() => {
                      setAnswers((prev) => ({ ...prev, [currentQuestion.question]: '' }));
                    }}
                    style={{
                      position: "absolute",
                      right: "10px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "18px",
                      color: "#999",
                      padding: "0",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                    title="Clear name"
                  >
                    ✕
                  </button>
                )}
              </div>
            ) : currentQuestion?.type === "textarea" ? (
              <div>
                {currentQuestion?.helperText && (
                  <p style={QUESTIONNAIRE_HELPER_TEXT_STYLE}>
                    {currentQuestion.helperText}
                  </p>
                )}
                {AI_GENERATE_QUESTION_KEYS.has(currentQuestion?.key) && (
                  <>
                    <div style={{
                      fontSize: "13px",
                      color: "#2c68a1",
                      marginBottom: "8px",
                      fontWeight: "600"
                    }}>
                      🤖 AI will generate this content
                    </div>
                    <button
                      type="button"
                      onClick={handleGenerateAI}
                      disabled={currentQuestion?.key != null && generatingAIForKeys.includes(currentQuestion.key)}
                      style={{
                        marginBottom: "12px",
                        padding: "10px 20px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff",
                        backgroundColor: currentQuestion?.key != null && generatingAIForKeys.includes(currentQuestion.key) ? "#999" : "#2c68a1",
                        border: "none",
                        borderRadius: "8px",
                        cursor: currentQuestion?.key != null && generatingAIForKeys.includes(currentQuestion.key) ? "not-allowed" : "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      {currentQuestion?.key != null && generatingAIForKeys.includes(currentQuestion.key) && (
                        <i className="fa fa-spinner fa-spin" style={{ fontSize: "16px" }} aria-hidden="true" />
                      )}
                      {currentQuestion?.key != null && generatingAIForKeys.includes(currentQuestion.key) ? "Generating…" : "Generate with AI"}
                    </button>
                  </>
                )}
                <TextareaAutosize
                  minRows={3}
                  maxRows={10}
                  style={{
                    width: "100%",
                    resize: "none",
                    padding:"10px 14px",
                    backgroundColor: "#fff"
                  }}
                  value={currentAnswer}
                  onChange={handleInputChange}
                  placeholder={currentQuestion.placeholder || "Write your answer..."}
                />
              </div>
            ) : currentQuestion?.type === "support_estimator" && currentQuestion?.fields ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {currentQuestion.fields.map((field, i) => {
                  const questionKey = currentQuestion.key;
                  const currentValue = supportEstimatorValues[questionKey]?.[field.key] || "";
                  
                  return (
                    <div key={i} style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "10px",
                      backgroundColor: "#f9f9f9"
                    }}>
                      <label style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        minWidth: "200px",
                        color: "#2c68a1"
                      }}>
                        {field.label}:
                      </label>
                      <input
                        type="number"
                        value={currentValue}
                        onChange={(e) => handleSupportEstimatorChange(field.key, e.target.value)}
                        placeholder="0"
                        min="0"
                        style={{
                          width: "120px",
                          padding: "10px 14px",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "1px solid #ccc",
                          backgroundColor: "#fff"
                        }}
                      />
                      <span style={{ fontSize: "16px", color: "#666" }}>
                        {field.unit}
                      </span>
                    </div>
                  );
                })}
                {(() => {
                  const questionKey = currentQuestion.key;
                  const values = supportEstimatorValues[questionKey] || {};
                  const totalMinutesPerWeek = Object.values(values).reduce((sum, val) => sum + (val || 0), 0);
                  const totalHoursPerYear = (totalMinutesPerWeek * 40) / 60;
                  
                  return totalMinutesPerWeek > 0 ? (
                    <div style={{
                      padding: "15px",
                      backgroundColor: totalHoursPerYear >= 25 ? "#d4edda" : "#fff3cd",
                      border: `2px solid ${totalHoursPerYear >= 25 ? "#c3e6cb" : "#ffc107"}`,
                      borderRadius: "10px",
                      marginTop: "10px"
                    }}>
                      <div style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: totalHoursPerYear >= 25 ? "#155724" : "#856404",
                        marginBottom: "5px"
                      }}>
                        Estimated Total: {totalHoursPerYear.toFixed(1)} hours per school year
                      </div>
                      <div style={{
                        fontSize: "14px",
                        color: totalHoursPerYear >= 25 ? "#155724" : "#856404"
                      }}>
                        {totalHoursPerYear >= 25 
                          ? "✓ This meets the 25+ hours threshold for a School-Based IEP"
                          : "⚠ This is below the 25+ hours threshold. Consider a Classroom Support Plan."}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            ) : currentQuestion?.type === "date_select" ? (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <select
                  value={currentAnswer.split(" ")[0] || ""}
                  onChange={(e) => {
                    const month = e.target.value;
                    const year = currentAnswer.split(" ")[1] || "";
                    setAnswers({
                      ...answers,
                      [currentQuestion?.question]: `${month} ${year}`.trim()
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    fontSize: "16px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value="">-- Select Month --</option>
                  {["January", "February", "March", "April", "May", "June", 
                    "July", "August", "September", "October", "November", "December"].map((month, i) => (
                    <option key={i} value={month}>{month}</option>
                  ))}
                </select>
                <select
                  value={currentAnswer.split(" ")[1] || ""}
                  onChange={(e) => {
                    const year = e.target.value;
                    const month = currentAnswer.split(" ")[0] || "";
                    setAnswers({
                      ...answers,
                      [currentQuestion?.question]: `${month} ${year}`.trim()
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    fontSize: "16px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value="">-- Select Year --</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map((year, i) => (
                    <option key={i} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            ) : currentQuestion?.type === "category_funding" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "8px" }}>
                <div>
                  <select
                    value={(categoryFundingValues[currentQuestion?.key] || {}).category || ""}
                    onChange={(e) => {
                      const key = currentQuestion?.key;
                      const val = e.target.value;
                      const prev = categoryFundingValues[key] || {};
                      const next = { ...prev, category: val };
                      if (val !== "Other") next.categoryOther = "";
                      setCategoryFundingValues(prevState => ({ ...prevState, [key]: next }));
                      const catLabel = val === "Other" ? (prev.categoryOther ? `Other: ${prev.categoryOther}` : "Other") : val;
                      setAnswers(prevAns => ({ ...prevAns, [currentQuestion?.question]: catLabel }));
                    }}
                    style={{ width: "100%", padding: "10px 14px", fontSize: "16px", borderRadius: "10px", border: "1px solid #ccc", backgroundColor: "#fff" }}
                  >
                    <option value="">Select a designation category</option>
                    {(currentQuestion?.categoryOptions || []).map((opt, i) => (
                      <option key={i} value={opt.label}>{opt.label}</option>
                    ))}
                  </select>
                  {currentQuestion?.allowOther && (categoryFundingValues[currentQuestion?.key] || {}).category === "Other" && (
                    <input
                      type="text"
                      value={(categoryFundingValues[currentQuestion?.key] || {}).categoryOther || ""}
                      onChange={(e) => {
                        const key = currentQuestion?.key;
                        const text = e.target.value;
                        const prev = categoryFundingValues[key] || {};
                        const next = { ...prev, categoryOther: text };
                        setCategoryFundingValues(prevState => ({ ...prevState, [key]: next }));
                        setAnswers(prevAns => ({ ...prevAns, [currentQuestion?.question]: text ? `Other: ${text}` : "Other" }));
                      }}
                      placeholder={currentQuestion?.otherPlaceholder || "Please specify"}
                      style={{ width: "100%", marginTop: "8px", padding: "10px 14px", fontSize: "16px", borderRadius: "10px", border: "1px solid #ccc", backgroundColor: "#fff" }}
                    />
                  )}
                </div>
              </div>
            ) : currentQuestion?.options && 
            currentQuestion.options.length > 0 && 
            !currentQuestion?.options.some((opt) => opt.children && opt.children.length > 0) ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <select
                  value={currentAnswer === "Other" || currentAnswer.startsWith("Other:") ? "Other" : currentAnswer}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "Other") {
                      setAnswers({ ...answers, [currentQuestion?.question]: "Other" });
                    } else {
                      setAnswers({ ...answers, [currentQuestion?.question]: val });
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "16px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">{currentQuestion?.key === "student_profile_grade" ? "Select a grade" : "Select an option"}</option>
                  {currentQuestion.options.map((opt, i) => (
                    <option key={i} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {currentQuestion?.allowOther && (currentAnswer === "Other" || currentAnswer.startsWith("Other:")) && (
                  <input
                    type="text"
                    value={
                      otherTextValues[currentQuestion?.key] !== undefined && otherTextValues[currentQuestion?.key] !== null
                        ? otherTextValues[currentQuestion?.key]
                        : (currentAnswer.startsWith("Other:") ? currentAnswer.replace(/^Other:\s*/, "") : "")
                    }
                    onChange={(e) => {
                      const text = e.target.value;
                      setOtherTextValues(prev => ({ ...prev, [currentQuestion?.key]: text }));
                      setAnswers(prev => ({ ...prev, [currentQuestion?.question]: text ? `Other: ${text}` : "Other" }));
                    }}
                    placeholder={currentQuestion?.otherPlaceholder || "Please specify"}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #ccc",
                      backgroundColor: "#fff",
                      marginLeft: "0",
                    }}
                  />
                )}
              </div>
            ) : (
              <div>
                <TextareaAutosize
                  minRows={1}
                  maxRows={6}
                  style={{ width: "100%", resize: "none", borderRadius: "8px", border: "1px solid rgb(204, 204, 204)" , padding:"10px 14px"}}
                  value={currentAnswer}
                  onChange={handleInputChange}
                  placeholder={currentQuestion?.placeholder || "Write your answer..."}
                />
              </div>
            )}
          </div>

          <div className="button-next">
            {/* Back Button - Always show */}
            <button
              style={{
                backgroundColor: "rgba(44, 104, 161, 1)",
                border: "2px solid rgba(44, 104, 161, 1)",
                borderRadius: "12px",
                cursor: "pointer",
                marginRight: "10px",
                fontWeight: "600"
              }}
              onClick={handleBack}
            >
              <i className="fa fa-arrow-circle-left" aria-hidden="true" style={{ marginRight: "8px", marginLeft: "0" }} />
              Back
            </button>

            {/* Next/Submit Button */}
            <button
              style={{ 
                backgroundColor: "rgba(44, 104, 161, 1)",
                flex: "1"
              }}
              onClick={() => handleNext(isQuestionRequired(currentQuestion) ? "true" : "false")}
            >
              {(() => {
                // Q.1 Designation question: never show "Learning Supports", only "Next"
                if (currentQuestion?.key === "designation_status") {
                  return "Next ";
                }
                // Q.2 Instructional Support Check: never show "Learning Supports", only "Next"
                if (currentQuestion?.key === "instructional_support_check") {
                  return "Next ";
                }
                // For the designation question (has_designation), show "Next" by default
                // and "Learning Supports" only after an option is selected and it's the last question
                if (currentQuestion?.key === "has_designation") {
                  if (currentIndex === filteredQuestionsList.length - 1 && isAnswerFilled) {
                    return FinalButton;
                  }
                  return "Next ";
                }
                // For other questions, show "Next" or FinalButton based on index
                return currentIndex < filteredQuestionsList.length - 1 ? "Next " : FinalButton;
              })()}
              <i className="fa fa-arrow-circle-right" aria-hidden="true" />
            </button>

            {/* Skip Button */}
            {!isQuestionRequired(currentQuestion) &&
              currentIndex < filteredQuestionsList.length - 1 && (
                <button
                  style={{
                    color: "rgba(44, 104, 161, 1)",
                    border: "none",
                    backgroundColor: "transparent",
                    float: "right",
                    marginRight: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleNext("false")}
                >
                  Skip{" "}
                  <i
                    className="fa fa-arrow-right simplearrow"
                    aria-hidden="true"
                  ></i>
                </button>
              )}
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningSupportsQuestionnaire;
